import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Spinner } from '../components/ui/spinner';
import { useToast } from '../hooks/use-toast';
import { formatCurrency, formatDate } from '../lib/i18n';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import {
  Wrench,
  Building2,
  Users,
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  LogOut,
  Globe,
  TrendingUp,
  MessageSquare,
  Eye,
  EyeOff,
  RefreshCw,
  KeyRound,
  Copy,
  RotateCw,
  Package,
  Plus,
  Pencil,
  Trash2,
  Megaphone,
  Droplet,
  Search,
  ChevronDown,
  ChevronUp,
  Sparkles,
  FileText,
  Truck,
  Upload,
  Monitor,
  Image,
} from 'lucide-react';
import SaasFinanceModule from './admin/SaasFinancePage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REMINDER_CHIPS = [
  { token: '{customer_name}', label: 'Customer Name' },
  { token: '{bike_info}', label: 'Vehicle Info' },
  { token: '{part_name}', label: 'Part Name' },
  { token: '{category_name}', label: 'Category' },
  { token: '{category_name_bn}', label: 'Category (BN)' },
  { token: '{purchase_date}', label: 'Purchase Date' },
  { token: '{days_passed}', label: 'Days Passed' },
  { token: '{interval_days}', label: 'Interval Days' },
  { token: '{garage_name}', label: 'Garage Name' },
  { token: '{garage_phone}', label: 'Garage Phone' },
  { token: '{garage_address}', label: 'Address' },
  { token: '{garage_website}', label: 'Website' },
  { token: '{today_date}', label: 'Today Date' },
  { token: '{job_number}', label: 'Job Number' },
];

const DEFAULT_TOPUP_BUNDLES = [
  { id: 'starter-100', name: 'Starter Pack', credits: 100, price_bdt: 190, is_active: true, sort_order: 1 },
  { id: 'growth-250', name: 'Growth Pack', credits: 250, price_bdt: 450, is_active: true, sort_order: 2 },
  { id: 'pro-500', name: 'Pro Pack', credits: 500, price_bdt: 850, is_active: true, sort_order: 3 },
  { id: 'elite-1000', name: 'Elite Pack', credits: 1000, price_bdt: 1600, is_active: true, sort_order: 4 },
];

function SuperAdminPreview({ template }) {
  const preview = useMemo(() => {
    if (!template) return 'Your message preview will appear here...';
    let msg = template;
    const sampleVars = {
      customer_name: 'Rahim Khan', bike_info: 'Honda CB Shine (DHA-1234)',
      part_name: 'Motul 10W40', category_name: 'Oils & Lubricants',
      category_name_bn: 'তেল ও লুব্রিক্যান্ট', purchase_date: '15 Dec 2025',
      interval_days: '90', garage_name: 'Demo Garage',
      garage_phone: '01712-345678', garage_address: 'Shop 12, Gulshan Avenue',
      garage_website: 'demo-garage.motopotti.com',
      today_date: new Date().toLocaleDateString('en-GB'), job_number: 'JOB-20251215-0042',
    };
    sampleVars.days_passed = String(Math.max(0, Math.floor((Date.now() - new Date('2025-12-15').getTime()) / 86400000)));
    for (const [key, val] of Object.entries(sampleVars)) {
      msg = msg.replace(new RegExp(`\\{${key}\\}`, 'g'), val);
    }
    return msg;
  }, [template]);

  return (
    <div className="rounded-xl border border-slate-200 bg-[#e5ddd5] p-3 mt-2">
      <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Live Preview</p>
      <div className="flex flex-col items-start">
        <div className="relative bg-[#dcf8c6] rounded-2xl rounded-tl-md px-3 py-2 max-w-[92%] shadow-sm">
          <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{preview}</p>
          <div className="absolute -left-1.5 top-2 w-3 h-3 bg-[#dcf8c6] rotate-45" />
          <p className="text-[9px] text-slate-400 text-right mt-1">{new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
        </div>
      </div>
    </div>
  );
}

export default function MotoPottiAdminPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { language, toggleLanguage, t } = useLanguage();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [demoRequests, setDemoRequests] = useState([]);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [renewMonths, setRenewMonths] = useState({});
  const [credentials, setCredentials] = useState({});  // { [tenantId]: { email, password, loading, showPw } }
  const [packageChanges, setPackageChanges] = useState({}); // { [tenantId]: newPackageId }
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { tenantId, name, input: '' }
  const [packages, setPackages] = useState([]);
  const [showPackageDialog, setShowPackageDialog] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [platformSettings, setPlatformSettings] = useState(null);
  const [topupRequests, setTopupRequests] = useState([]);
  const [savingPlatform, setSavingPlatform] = useState(false);
  const [topupBundles, setTopupBundles] = useState([]);
  const [smsTemplates, setSmsTemplates] = useState([]);
  // Consumable categories state
  const [sysCategories, setSysCategories] = useState([]);
  const [catLoading, setCatLoading] = useState(false);
  const [showCatDialog, setShowCatDialog] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [catForm, setCatForm] = useState({ name: '', default_interval_days: 90, keywords: '', message_template: '', is_active: true });
  const [expandedCat, setExpandedCat] = useState(null);
  const [unmatchedReport, setUnmatchedReport] = useState(null);
  const [pendingTemplates, setPendingTemplates] = useState([]);
  const [approvingTemplate, setApprovingTemplate] = useState(null);
  const [renewals, setRenewals] = useState([]);
  // AI & Suppliers tab state
  const [aiStats, setAiStats] = useState(null);
  const [aiStatsLoading, setAiStatsLoading] = useState(false);
  const [supplierList, setSupplierList] = useState([]);
  const [supplierLoading, setSupplierLoading] = useState(false);
  const [aiSubTab, setAiSubTab] = useState('overview'); // overview | suppliers | batches | sms-costs
  const [aiSmsStats, setAiSmsStats] = useState(null);
  const [aiSmsLoading, setAiSmsLoading] = useState(false);
  // Landing page stats state
  const [landingStatsMode, setLandingStatsMode] = useState('real'); // "real" or "custom"
  const [landingHeroStats, setLandingHeroStats] = useState({ active_garages: '', starting_price: '', support: '24/7', revenue_handled: '' });
  const [landingNetworkStats, setLandingNetworkStats] = useState({ active_garages: '', zones_covered: '', cloud_support: '24/7', uptime: '99.9%', jobs_processed: '', avg_rating: '4.8' });
  const [savingLandingStats, setSavingLandingStats] = useState(false);
  // Hardware state
  const [hwItems, setHwItems] = useState([]);
  const [hwLoading, setHwLoading] = useState(false);
  const [hwForm, setHwForm] = useState({ name: '', description: '', price: '', bundle_tag: '', category: '' });
  const [hwEditing, setHwEditing] = useState(null);
  const [hwDialog, setHwDialog] = useState(false);
  const [hwOrders, setHwOrders] = useState([]);
  const [hwOrdersLoading, setHwOrdersLoading] = useState(false);
  const [packageForm, setPackageForm] = useState({
    name: '', name_bn: '', slug: '',
    description: '', description_bn: '',
  tagline: '', tagline_bn: '',
  regular_price_monthly: '', discounted_price_monthly: '',
  regular_price_half_yearly: '', discounted_price_half_yearly: '',
  regular_price_yearly: '', discounted_price_yearly: '',
  setup_fee: '',
  features: '', features_bn: '', is_active: true
  });

  useEffect(() => {
    if (user?.role !== 'super_admin') {
      navigate('/login');
      return;
    }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      const [statsRes, tenantsRes, subsRes, demosRes, pkgRes, renewalsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/tenants`),
        axios.get(`${API}/subscriptions`),
        axios.get(`${API}/demo-requests`),
        axios.get(`${API}/admin/packages`),
        axios.get(`${API}/renewals`).catch(() => ({ data: [] }))
      ]);
      setStats(statsRes.data);
      setTenants(tenantsRes.data);
      setSubscriptions(subsRes.data);
      setDemoRequests(demosRes.data);
      setPackages(pkgRes.data);
      setRenewals(renewalsRes.data);
      // Fetch marketing data (non-blocking)
      try {
        const [settingsRes, topupsRes] = await Promise.all([
          axios.get(`${API}/admin/platform-settings`),
          axios.get(`${API}/admin/credit-topup-requests?status=pending`)
        ]);
        setPlatformSettings(settingsRes.data);
        setTopupBundles(Array.isArray(settingsRes.data?.topup_bundles) ? settingsRes.data.topup_bundles : []);
        setSmsTemplates(Array.isArray(settingsRes.data?.sms_templates) ? settingsRes.data.sms_templates : []);
        setTopupRequests(topupsRes.data);
      } catch (e) {
        console.error('Failed to fetch marketing data:', e);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  // ===== Consumable Categories Functions =====
  const fetchSysCategories = async () => {
    setCatLoading(true);
    try {
      const res = await axios.get(`${API}/admin/consumable-categories`);
      setSysCategories(res.data || []);
    } catch (e) {
      console.error('Failed to fetch system categories:', e);
    } finally { setCatLoading(false); }
  };

  const openCatCreate = () => {
    setEditingCat(null);
    setCatForm({ name: '', default_interval_days: 90, keywords: '', message_template: '', is_active: true });
    setShowCatDialog(true);
  };

  const openCatEdit = (cat) => {
    setEditingCat(cat);
    setCatForm({
      name: cat.name || '',
      default_interval_days: cat.default_interval_days || 90,
      keywords: (cat.keywords || []).join(', '),
      message_template: cat.message_template || '',
      is_active: cat.is_active !== false,
    });
    setShowCatDialog(true);
  };

  const saveCat = async () => {
    const payload = {
      name: catForm.name,
      default_interval_days: parseInt(catForm.default_interval_days) || 90,
      keywords: catForm.keywords.split(',').map(k => k.trim()).filter(Boolean),
      message_template: catForm.message_template || null,
      is_active: catForm.is_active,
    };
    try {
      if (editingCat) {
        await axios.put(`${API}/admin/consumable-categories/${editingCat.id}`, payload);
        toast({ title: 'Category updated' });
      } else {
        await axios.post(`${API}/admin/consumable-categories`, payload);
        toast({ title: 'Category created' });
      }
      setShowCatDialog(false);
      fetchSysCategories();
    } catch (e) {
      toast({ title: e.response?.data?.detail || 'Failed to save', variant: 'destructive' });
    }
  };

  const deleteCat = async (catId) => {
    if (!window.confirm('Delete this system category? Tenant copies will NOT be affected.')) return;
    try {
      await axios.delete(`${API}/admin/consumable-categories/${catId}`);
      toast({ title: 'Category deleted' });
      fetchSysCategories();
    } catch (e) {
      toast({ title: 'Failed to delete', variant: 'destructive' });
    }
  };

  const syncToTenants = async () => {
    try {
      const res = await axios.post(`${API}/admin/consumable-categories/sync-to-tenants`);
      toast({ title: `Synced to ${res.data?.tenants_synced || 0} tenants` });
    } catch (e) {
      toast({ title: 'Sync failed', variant: 'destructive' });
    }
  };

  const fetchUnmatchedReport = async () => {
    try {
      const res = await axios.get(`${API}/admin/consumable-categories/unmatched-parts-report`);
      setUnmatchedReport(res.data);
    } catch (e) {
      toast({ title: 'Failed to fetch report', variant: 'destructive' });
    }
  };

  const fetchPendingTemplates = async () => {
    try {
      const res = await axios.get(`${API}/admin/consumable-categories/pending-templates`);
      setPendingTemplates(res.data || []);
    } catch (e) {
      console.error('Failed to fetch pending templates', e);
    }
  };

  const handleTemplateApproval = async (categoryId, tenantId, action) => {
    setApprovingTemplate(`${categoryId}-${action}`);
    try {
      await axios.put(`${API}/admin/consumable-categories/${categoryId}/approve-template`, { action, tenant_id: tenantId });
      toast({ title: action === 'approve' ? 'Template approved' : 'Template rejected' });
      fetchPendingTemplates();
    } catch (e) {
      toast({ title: 'Action failed', variant: 'destructive' });
    } finally {
      setApprovingTemplate(null);
    }
  };

  // Load categories when consumables tab is activated
  useEffect(() => {
    if (activeTab === 'consumables' && sysCategories.length === 0) {
      fetchSysCategories();
    }
    if (activeTab === 'consumables') {
      fetchPendingTemplates();
    }
  }, [activeTab]);

  // AI & Suppliers tab functions
  const fetchAiStats = async () => {
    setAiStatsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/ai-usage-stats`);
      setAiStats(res.data);
    } catch (e) {
      console.error('Failed to fetch AI stats:', e);
    } finally { setAiStatsLoading(false); }
  };

  const fetchSuppliers = async () => {
    setSupplierLoading(true);
    try {
      const res = await axios.get(`${API}/admin/supplier-contacts`);
      setSupplierList(res.data || []);
    } catch (e) {
      console.error('Failed to fetch suppliers:', e);
    } finally { setSupplierLoading(false); }
  };

  const fetchAiSmsStats = async () => {
    setAiSmsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/ai-sms-stats`);
      setAiSmsStats(res.data);
    } catch (e) {
      console.error('Failed to fetch AI SMS stats:', e);
    } finally { setAiSmsLoading(false); }
  };

  useEffect(() => {
    if (activeTab === 'ai-suppliers') {
      if (!aiStats) fetchAiStats();
      if (supplierList.length === 0) fetchSuppliers();
    }
    if (activeTab === 'ai-suppliers' && aiSubTab === 'sms-costs' && !aiSmsStats) {
      fetchAiSmsStats();
    }
    if (activeTab === 'landing-page') {
      fetchLandingStats();
    }
    if (activeTab === 'hardware') {
      fetchHardware();
      fetchHwOrders();
    }
  }, [activeTab]);

  // Refetch when AI sub-tab changes to sms-costs
  useEffect(() => {
    if (activeTab === 'ai-suppliers' && aiSubTab === 'sms-costs' && !aiSmsStats) {
      fetchAiSmsStats();
    }
  }, [activeTab, aiSubTab]);

  const fetchLandingStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/platform-settings`);
      const s = res.data;
      setLandingStatsMode(s.landing_stats_mode || 'real');
      if (s.landing_hero_stats) setLandingHeroStats(prev => ({ ...prev, ...s.landing_hero_stats }));
      if (s.landing_network_stats) setLandingNetworkStats(prev => ({ ...prev, ...s.landing_network_stats }));
    } catch (e) { console.error('Failed to fetch landing stats:', e); }
  };

  const saveLandingStats = async () => {
    setSavingLandingStats(true);
    try {
      await axios.put(`${API}/admin/platform-settings`, {
        landing_stats_mode: landingStatsMode,
        landing_hero_stats: landingHeroStats,
        landing_network_stats: landingNetworkStats,
      });
      toast({ title: 'Landing page stats saved!' });
    } catch (e) {
      toast({ title: 'Failed to save', variant: 'destructive' });
    } finally { setSavingLandingStats(false); }
  };

  // Hardware CRUD
  const fetchHardware = async () => {
    setHwLoading(true);
    try {
      const res = await axios.get(`${API}/admin/hardware`);
      setHwItems(res.data || []);
    } catch (e) { console.error(e); }
    finally { setHwLoading(false); }
  };
  const saveHardwareItem = async () => {
    try {
      if (hwEditing) {
        await axios.put(`${API}/admin/hardware/${hwEditing}`, hwForm);
        toast({ title: 'Hardware item updated!' });
      } else {
        await axios.post(`${API}/admin/hardware`, hwForm);
        toast({ title: 'Hardware item created!' });
      }
      setHwDialog(false);
      setHwForm({ name: '', description: '', price: '', bundle_tag: '', category: '' });
      setHwEditing(null);
      fetchHardware();
    } catch (e) {
      toast({ title: 'Failed to save', variant: 'destructive' });
    }
  };
  const deleteHardwareItem = async (id) => {
    if (!window.confirm('Delete this hardware item?')) return;
    try {
      await axios.delete(`${API}/admin/hardware/${id}`);
      toast({ title: 'Deleted' });
      fetchHardware();
    } catch (e) {
      toast({ title: 'Failed to delete', variant: 'destructive' });
    }
  };
  const uploadHardwareImage = async (itemId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API}/admin/hardware/${itemId}/image`, fd);
      toast({ title: 'Image uploaded!' });
      fetchHardware();
    } catch (e) {
      toast({ title: 'Upload failed', variant: 'destructive' });
    }
  };
  const fetchHwOrders = async () => {
    setHwOrdersLoading(true);
    try {
      const res = await axios.get(`${API}/admin/hardware-orders`);
      setHwOrders(res.data || []);
    } catch (e) { console.error(e); }
    finally { setHwOrdersLoading(false); }
  };
  const updateHwOrderStatus = async (orderId, status) => {
    try {
      await axios.put(`${API}/admin/hardware-orders/${orderId}/status`, { status });
      toast({ title: `Order ${status}` });
      fetchHwOrders();
    } catch (e) { toast({ title: 'Failed', variant: 'destructive' }); }
  };

  const verifySubscription = async (subscriptionId) => {
    try {
      const response = await axios.put(`${API}/subscriptions/${subscriptionId}/verify`);
      toast({ 
        title: 'Subscription Verified!',
        description: `Login: ${response.data.admin_email} / ${response.data.admin_password}`
      });
      fetchData();
    } catch (error) {
      toast({ title: 'Failed to verify', variant: 'destructive' });
    }
  };

  const rejectSubscription = async (subscriptionId) => {
    try {
      await axios.put(`${API}/subscriptions/${subscriptionId}/reject`);
      toast({ title: 'Subscription Rejected' });
      fetchData();
    } catch (error) {
      toast({ title: 'Failed to reject', variant: 'destructive' });
    }
  };

  const updateDemoStatus = async (requestId, status) => {
    try {
      await axios.put(`${API}/demo-requests/${requestId}/status?status=${status}`);
      toast({ title: 'Status Updated' });
      fetchData();
    } catch (error) {
      toast({ title: 'Failed to update', variant: 'destructive' });
    }
  };

  const toggleTenantStatus = async (tenantId) => {
    try {
      await axios.put(`${API}/admin/tenants/${tenantId}/toggle`);
      toast({ title: 'Tenant status updated' });
      fetchData();
    } catch (error) {
      toast({ title: 'Failed to update', variant: 'destructive' });
    }
  };

  const renewSubscription = async (tenantId) => {
    const months = parseInt(renewMonths[tenantId]) || 1;
    try {
      const res = await axios.put(`${API}/admin/tenants/${tenantId}/renew?months=${months}`);
      toast({ title: `Subscription renewed for ${months} month(s)`, description: `New end: ${new Date(res.data.new_end_date).toLocaleDateString()}` });
      fetchData();
    } catch (error) {
      toast({ title: 'Failed to renew', variant: 'destructive' });
    }
  };

  const permanentlyDeleteTenant = async (tenantId) => {
    try {
      const res = await axios.delete(`${API}/admin/tenants/${tenantId}`);
      toast({ title: 'Tenant Permanently Deleted', description: res.data.message });
      setDeleteConfirm(null);
      fetchData();
    } catch (error) {
      toast({ title: 'Failed to delete tenant', description: error.response?.data?.detail || 'Error', variant: 'destructive' });
    }
  };

  const fetchCredentials = async (tenantId) => {
    // Toggle off if already showing
    if (credentials[tenantId]?.email) {
      setCredentials(prev => { const copy = { ...prev }; delete copy[tenantId]; return copy; });
      return;
    }
    setCredentials(prev => ({ ...prev, [tenantId]: { loading: true } }));
    try {
      const res = await axios.get(`${API}/admin/tenants/${tenantId}/owner-credentials`);
      setCredentials(prev => ({ ...prev, [tenantId]: { email: res.data.email, name: res.data.name, password: res.data.password || null, loading: false, showPw: false } }));
    } catch (err) {
      toast({ title: 'Failed to fetch credentials', variant: 'destructive' });
      setCredentials(prev => { const copy = { ...prev }; delete copy[tenantId]; return copy; });
    }
  };

  const resetOwnerPassword = async (tenantId) => {
    setCredentials(prev => ({ ...prev, [tenantId]: { ...prev[tenantId], loading: true } }));
    try {
      const res = await axios.post(`${API}/admin/tenants/${tenantId}/reset-owner-password`);
      setCredentials(prev => ({ ...prev, [tenantId]: { ...prev[tenantId], email: res.data.email, password: res.data.new_password, loading: false, showPw: true } }));
      toast({ title: 'Password reset!', description: 'New password generated successfully.' });
    } catch (err) {
      toast({ title: 'Failed to reset password', variant: 'destructive' });
      setCredentials(prev => ({ ...prev, [tenantId]: { ...prev[tenantId], loading: false } }));
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({ title: 'Copied!' });
  };

  const sanitizeTopupBundles = (bundles) => {
    return (bundles || [])
      .map((bundle, idx) => ({
        id: bundle.id || `bundle-${Date.now()}-${idx}`,
        name: String(bundle.name || '').trim(),
        credits: Math.max(0, parseInt(bundle.credits) || 0),
        price_bdt: Math.max(0, parseFloat(bundle.price_bdt) || 0),
        is_active: bundle.is_active !== false,
        sort_order: parseInt(bundle.sort_order) || idx + 1,
      }))
      .filter(bundle => bundle.name && bundle.credits > 0);
  };

  const updateTopupBundle = (index, field, value) => {
    setTopupBundles(prev => prev.map((bundle, i) => i === index ? { ...bundle, [field]: value } : bundle));
  };

  const addTopupBundleRow = () => {
    setTopupBundles(prev => ([
      ...prev,
      {
        id: `bundle-${Date.now()}-${prev.length}`,
        name: '',
        credits: 100,
        price_bdt: 200,
        is_active: true,
        sort_order: prev.length + 1,
      },
    ]));
  };

  const removeTopupBundleRow = (index) => {
    setTopupBundles(prev => prev.filter((_, i) => i !== index));
  };

  // SMS Templates helpers
  const SMS_TEMPLATE_CATEGORIES = ['Promotions', 'Service', 'Seasonal', 'Lifecycle', 'Dues', 'General'];
  const addSmsTemplate = () => {
    setSmsTemplates(prev => [...prev, { id: `tmpl-${Date.now()}`, name: '', body: '', category: 'General', is_active: true, sort_order: prev.length + 1 }]);
  };
  const updateSmsTemplate = (index, field, value) => {
    setSmsTemplates(prev => prev.map((t, i) => i === index ? { ...t, [field]: value } : t));
  };
  const removeSmsTemplate = (index) => {
    setSmsTemplates(prev => prev.filter((_, i) => i !== index));
  };

  const seedDefaultTopupBundles = async () => {
    setSavingPlatform(true);
    try {
      const seeded = DEFAULT_TOPUP_BUNDLES.map((bundle) => ({ ...bundle }));
      const { sms_provider, sms_api_key, sms_api_url, sms_sender_id, credit_price_bdt, is_sms_enabled } = platformSettings;
      const res = await axios.put(`${API}/admin/platform-settings`, {
        sms_provider, sms_api_key, sms_api_url, sms_sender_id,
        credit_price_bdt, is_sms_enabled,
        topup_bundles: seeded,
      });
      setPlatformSettings(res.data);
      setTopupBundles(Array.isArray(res.data?.topup_bundles) ? res.data.topup_bundles : seeded);
      toast({ title: 'Default top-up bundles seeded' });
    } catch (err) {
      toast({ title: 'Failed to seed bundles', variant: 'destructive' });
    } finally {
      setSavingPlatform(false);
    }
  };

  const changeTenantPackage = async (tenantId) => {
    const newPkgId = packageChanges[tenantId];
    if (!newPkgId) return;
    try {
      const { data } = await axios.put(`${API}/admin/tenants/${tenantId}/change-package`, { package_id: newPkgId });
      toast({ title: data.message || 'Package changed' });
      setTenants(prev => prev.map(t => t.id === tenantId ? { ...t, subscription_plan: newPkgId } : t));
      setPackageChanges(prev => ({ ...prev, [tenantId]: undefined }));
    } catch (err) {
      toast({ title: 'Failed to change package', description: err.response?.data?.detail || 'Error', variant: 'destructive' });
    }
  };

  const getPackageName = (pkgId) => {
    if (!pkgId) return null;
    const pkg = packages.find(p => p.id === pkgId || p.slug === pkgId);
    return pkg ? (language === 'bn' && pkg.name_bn ? pkg.name_bn : pkg.name) : null;
  };

  const openCreatePackage = () => {
    setEditingPackage(null);
    setPackageForm({
      name: '', name_bn: '', slug: '',
      description: '', description_bn: '',
      tagline: '', tagline_bn: '',
      regular_price_monthly: '', discounted_price_monthly: '',
      regular_price_half_yearly: '', discounted_price_half_yearly: '',
      regular_price_yearly: '', discounted_price_yearly: '',
      setup_fee: '',
      features: '', features_bn: '',
      is_popular: false, sort_order: packages.length, is_active: true
    });
    setShowPackageDialog(true);
  };

  const openEditPackage = (pkg) => {
    setEditingPackage(pkg);
    setPackageForm({
      name: pkg.name || '',
      name_bn: pkg.name_bn || '',
      slug: pkg.slug || '',
      description: pkg.description || '',
      description_bn: pkg.description_bn || '',
      tagline: pkg.tagline || '',
      tagline_bn: pkg.tagline_bn || '',
      regular_price_monthly: pkg.regular_price_monthly ?? '',
      discounted_price_monthly: pkg.discounted_price_monthly ?? '',
      regular_price_half_yearly: pkg.regular_price_half_yearly ?? '',
      discounted_price_half_yearly: pkg.discounted_price_half_yearly ?? '',
      regular_price_yearly: pkg.regular_price_yearly ?? '',
      discounted_price_yearly: pkg.discounted_price_yearly ?? '',
      setup_fee: pkg.setup_fee ?? '',
      features: (pkg.features || []).join('\n'),
      features_bn: (pkg.features_bn || []).join('\n'),
      is_popular: pkg.is_popular || false,
      sort_order: pkg.sort_order ?? 0,
      is_active: pkg.is_active !== false
    });
    setShowPackageDialog(true);
  };

  const savePackage = async () => {
    const payload = {
      name: packageForm.name,
      name_bn: packageForm.name_bn || null,
      slug: packageForm.slug,
      description: packageForm.description || null,
      description_bn: packageForm.description_bn || null,
      tagline: packageForm.tagline || null,
      tagline_bn: packageForm.tagline_bn || null,
      regular_price_monthly: parseFloat(packageForm.regular_price_monthly) || 0,
      discounted_price_monthly: packageForm.discounted_price_monthly ? parseFloat(packageForm.discounted_price_monthly) : null,
      regular_price_half_yearly: packageForm.regular_price_half_yearly ? parseFloat(packageForm.regular_price_half_yearly) : null,
      discounted_price_half_yearly: packageForm.discounted_price_half_yearly ? parseFloat(packageForm.discounted_price_half_yearly) : null,
      regular_price_yearly: parseFloat(packageForm.regular_price_yearly) || 0,
      discounted_price_yearly: packageForm.discounted_price_yearly ? parseFloat(packageForm.discounted_price_yearly) : null,
      setup_fee: packageForm.setup_fee !== '' ? parseFloat(packageForm.setup_fee) || 0 : 0,
      features: packageForm.features.split('\n').map(f => f.trim()).filter(Boolean),
      features_bn: packageForm.features_bn.split('\n').map(f => f.trim()).filter(Boolean),
      is_popular: packageForm.is_popular,
      sort_order: parseInt(packageForm.sort_order) || 0,
      is_active: packageForm.is_active
    };
    try {
      if (editingPackage) {
        await axios.put(`${API}/admin/packages/${editingPackage.id}`, payload);
        toast({ title: 'Package updated!' });
      } else {
        await axios.post(`${API}/admin/packages`, payload);
        toast({ title: 'Package created!' });
      }
      setShowPackageDialog(false);
      fetchData();
    } catch (error) {
      toast({ title: error.response?.data?.detail || 'Failed to save package', variant: 'destructive' });
    }
  };

  const deletePackage = async (pkgId) => {
    if (!window.confirm('Are you sure you want to delete this package?')) return;
    try {
      await axios.delete(`${API}/admin/packages/${pkgId}`);
      toast({ title: 'Package deleted' });
      fetchData();
    } catch (error) {
      toast({ title: 'Failed to delete', variant: 'destructive' });
    }
  };

  const seedPackages = async () => {
    try {
      await axios.post(`${API}/admin/packages/seed`);
      toast({ title: 'Default packages created!' });
      fetchData();
    } catch (error) {
      toast({ title: error.response?.data?.detail || 'Failed to seed', variant: 'destructive' });
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Spinner size="lg" />
      </div>
    );
  }

  const pendingSubscriptions = subscriptions.filter(s => s.payment_status === 'pending');
  const pendingDemos = demoRequests.filter(d => d.status === 'pending');
  const pendingRenewals = renewals.filter(r => r.status === 'pending');

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-600 rounded-lg flex items-center justify-center">
                <Wrench className="w-6 h-6" />
              </div>
              <div>
                <span className="font-bold text-lg">MotoPotti Admin</span>
                <p className="text-xs text-slate-400">Super Admin Panel</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={toggleLanguage} className="text-white">
                <Globe className="w-4 h-4 mr-2" />
                {language === 'en' ? 'বাংলা' : 'English'}
              </Button>
              <span className="text-sm text-slate-300">{user?.name}</span>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-white">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-8">
            <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="subscriptions" className="relative">
              Subscriptions
              {pendingSubscriptions.length > 0 && (
                <Badge className="ml-2 bg-red-500">{pendingSubscriptions.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="tenants">Garages</TabsTrigger>
            <TabsTrigger value="demos" className="relative">
              Demo Requests
              {pendingDemos.length > 0 && (
                <Badge className="ml-2 bg-orange-500">{pendingDemos.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="packages" className="gap-1">
              {/* <Package className="w-4 h-4" /> */}
              Packages
            </TabsTrigger>
            <TabsTrigger value="finance" className="gap-1"> Finance</TabsTrigger>
            <TabsTrigger value="marketing" className="gap-1">Marketing</TabsTrigger>
            <TabsTrigger value="renewals" className="relative">
              Renewals
              {pendingRenewals.length > 0 && (
                <Badge className="ml-2 bg-emerald-500">{pendingRenewals.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="consumables" className="gap-1">Consumables</TabsTrigger>
            <TabsTrigger value="ai-suppliers" className="gap-1">AI & Suppliers</TabsTrigger>
            <TabsTrigger value="landing-page" className="gap-1">Landing Page</TabsTrigger>
            <TabsTrigger value="hardware" className="gap-1">Hardware</TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
                <CardContent className="p-6">
                  <Building2 className="w-8 h-8 mb-2 opacity-80" />
                  <p className="text-emerald-100">Active Garages</p>
                  <p className="text-3xl font-bold">{stats?.active_tenants || 0}</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                <CardContent className="p-6">
                  <TrendingUp className="w-8 h-8 mb-2 opacity-80" />
                  <p className="text-blue-100">Total Revenue</p>
                  <p className="text-3xl font-bold">{formatCurrency(stats?.total_revenue || 0, language)}</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
                <CardContent className="p-6">
                  <Clock className="w-8 h-8 mb-2 opacity-80" />
                  <p className="text-orange-100">Pending Subscriptions</p>
                  <p className="text-3xl font-bold">{stats?.pending_subscriptions || 0}</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
                <CardContent className="p-6">
                  <MessageSquare className="w-8 h-8 mb-2 opacity-80" />
                  <p className="text-purple-100">Demo Requests</p>
                  <p className="text-3xl font-bold">{stats?.pending_demos || 0}</p>
                </CardContent>
              </Card>
            </div>

            {/* Recent Pending Items */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Pending Subscriptions</CardTitle>
                </CardHeader>
                <CardContent>
                  {pendingSubscriptions.length > 0 ? (
                    <div className="space-y-3">
                      {pendingSubscriptions.slice(0, 5).map((sub) => (
                        <div key={sub.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div>
                            <p className="font-semibold">{sub.garage_name}</p>
                            <p className="text-sm text-slate-500">{sub.payment_method} - {formatCurrency(sub.amount, language)}</p>
                          </div>
                          <Button size="sm" onClick={() => setActiveTab('subscriptions')}>
                            Review
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500 text-center py-8">No pending subscriptions</p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Recent Garages</CardTitle>
                </CardHeader>
                <CardContent>
                  {tenants.length > 0 ? (
                    <div className="space-y-3">
                      {tenants.slice(0, 5).map((tenant) => (
                        <div key={tenant.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div>
                            <p className="font-semibold">{tenant.name}</p>
                            <p className="text-sm text-slate-500">/{tenant.slug}</p>
                          </div>
                          <Badge className={tenant.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                            {tenant.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500 text-center py-8">No garages yet</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Subscriptions Tab */}
          <TabsContent value="subscriptions">
            <Card>
              <CardHeader>
                <CardTitle>Subscription Requests</CardTitle>
              </CardHeader>
              <CardContent>
                {subscriptions.length > 0 ? (
                  <div className="space-y-4">
                    {subscriptions.map((sub) => (
                      <div 
                        key={sub.id} 
                        className={`p-4 rounded-lg border-2 ${
                          sub.payment_status === 'pending' ? 'border-orange-300 bg-orange-50' :
                          sub.payment_status === 'verified' ? 'border-green-300 bg-green-50' :
                          'border-red-300 bg-red-50'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-bold text-lg">{sub.garage_name}</h3>
                              <Badge className={
                                sub.payment_status === 'pending' ? 'bg-orange-500' :
                                sub.payment_status === 'verified' ? 'bg-green-500' : 'bg-red-500'
                              }>
                                {sub.payment_status}
                              </Badge>
                            </div>
                            <p className="text-slate-600">{sub.owner_name} • {sub.email}</p>
                            <p className="text-slate-600">{sub.phone}</p>
                            <p className="text-slate-600">{sub.address}</p>
                            <div className="mt-2 flex flex-wrap gap-2">
                              <Badge variant="outline">{getPackageName(sub.plan) || sub.plan} plan</Badge>
                              <Badge variant="outline">{sub.billing_cycle}</Badge>
                              <Badge variant="outline">{sub.payment_method}</Badge>
                              <Badge variant="outline" className="font-bold">{formatCurrency(sub.amount, language)}</Badge>
                            </div>
                            {sub.transaction_id && (
                              <p className="mt-2 text-sm font-mono bg-slate-100 p-2 rounded">
                                TRX: {sub.transaction_id} | From: {sub.payment_phone}
                              </p>
                            )}
                            <p className="text-xs text-slate-400 mt-2">
                              Submitted: {formatDate(sub.created_at, language)}
                            </p>
                          </div>

                          {sub.payment_status === 'pending' && (
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => verifySubscription(sub.id)}
                                data-testid={`verify-${sub.id}`}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Verify
                              </Button>
                              <Button 
                                size="sm" 
                                variant="destructive"
                                onClick={() => rejectSubscription(sub.id)}
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                Reject
                              </Button>
                            </div>
                          )}

                          {sub.payment_status === 'verified' && sub.tenant_id && (
                            <Link to={`/${subscriptions.find(s => s.id === sub.id)?.slug || ''}`} target="_blank">
                              <Button size="sm" variant="outline">
                                <Eye className="w-4 h-4 mr-1" />
                                View Garage
                              </Button>
                            </Link>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-8">No subscription requests yet</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Renewals Tab */}
          <TabsContent value="renewals">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  Renewal Requests
                  {pendingRenewals.length > 0 && <Badge className="bg-emerald-500">{pendingRenewals.length} pending</Badge>}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {renewals.length > 0 ? (
                  <div className="space-y-4">
                    {renewals.map((r) => (
                      <div
                        key={r.id}
                        className={`p-4 rounded-lg border-2 ${
                          r.status === 'pending' ? 'border-amber-300 bg-amber-50' :
                          r.status === 'approved' ? 'border-green-300 bg-green-50' :
                          'border-red-300 bg-red-50'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-bold text-lg">{r.tenant_name || r.tenant_slug}</h3>
                              <Badge className={
                                r.status === 'pending' ? 'bg-amber-500' :
                                r.status === 'approved' ? 'bg-green-500' : 'bg-red-500'
                              }>
                                {r.status}
                              </Badge>
                            </div>
                            <div className="flex flex-wrap gap-2 mt-1">
                              <Badge variant="outline">{getPackageName(r.plan) || r.plan} plan</Badge>
                              <Badge variant="outline">{r.billing_cycle}</Badge>
                              <Badge variant="outline">{r.payment_method}</Badge>
                              <Badge variant="outline" className="font-bold">৳{r.amount?.toLocaleString()}</Badge>
                            </div>
                            {r.transaction_id && (
                              <p className="mt-2 text-sm font-mono bg-slate-100 p-2 rounded">
                                TRX: {r.transaction_id}{r.payment_phone ? ` | From: ${r.payment_phone}` : ''}
                              </p>
                            )}
                            <p className="text-xs text-slate-400 mt-2">
                              Submitted: {formatDate(r.created_at, language)}
                            </p>
                          </div>

                          {r.status === 'pending' && (
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700"
                                onClick={async () => {
                                  try {
                                    await axios.put(`${API}/renewals/${r.id}/approve`);
                                    toast({ title: 'Renewal Approved!', description: `${r.tenant_name} extended (${r.billing_cycle})` });
                                    fetchData();
                                  } catch (err) { toast({ title: 'Failed to approve', variant: 'destructive' }); }
                                }}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={async () => {
                                  try {
                                    await axios.put(`${API}/renewals/${r.id}/reject`);
                                    toast({ title: 'Renewal Rejected' });
                                    fetchData();
                                  } catch (err) { toast({ title: 'Failed to reject', variant: 'destructive' }); }
                                }}
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                Reject
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-8">No renewal requests yet</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tenants Tab */}
          <TabsContent value="tenants">
            <Card>
              <CardHeader>
                <CardTitle>Registered Garages ({tenants.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {tenants.length > 0 ? (
                  <div className="space-y-4">
                    {tenants.map((tenant) => {
                      const currentPkgName = getPackageName(tenant.subscription_plan);
                      const currentPkg = packages.find(p => p.id === tenant.subscription_plan);
                      return (
                      <div key={tenant.id} className="p-4 bg-slate-50 rounded-lg">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <h3 className="font-bold text-lg">{tenant.name}</h3>
                              {tenant.name_bn && <span className="text-slate-500">({tenant.name_bn})</span>}
                              <Badge className={tenant.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                                {tenant.is_active ? 'Active' : 'Inactive'}
                              </Badge>
                              <Badge className={
                                tenant.subscription_status === 'active' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-800'
                              }>
                                {tenant.subscription_status}
                              </Badge>
                            </div>
                            <p className="text-slate-600">{tenant.email} • {tenant.phone}</p>
                            <p className="text-slate-600">{tenant.address}</p>
                            <p className="text-sm text-emerald-600 font-medium mt-2">
                              URL: /{tenant.slug}
                            </p>
                            {tenant.subscription_end_date && (
                              <p className="text-xs text-slate-400 mt-1">
                                Subscription ends: {formatDate(tenant.subscription_end_date, language)}
                              </p>
                            )}

                            {/* Package Info */}
                            <div className="mt-3 p-3 bg-white border border-indigo-100 rounded-lg">
                              <div className="flex items-center gap-2 mb-2">
                                <Package className="w-4 h-4 text-indigo-500" />
                                <span className="text-sm font-semibold text-slate-700">Current Package</span>
                              </div>
                              {currentPkgName ? (
                                <div className="space-y-1.5">
                                  <div className="flex items-center gap-2">
                                    <Badge className="bg-indigo-100 text-indigo-800 rounded-full px-3">{currentPkgName}</Badge>
                                    {currentPkg?.is_popular && <Badge className="bg-amber-100 text-amber-800 rounded-full px-2 text-xs">Popular</Badge>}
                                  </div>
                                  {currentPkg && (
                                    <div className="text-xs text-slate-500 flex flex-wrap gap-3">
                                      <span>Monthly: ৳{currentPkg.discounted_price_monthly || currentPkg.regular_price_monthly}</span>
                                      {currentPkg.discounted_price_half_yearly && <span>Half-Yearly: ৳{currentPkg.discounted_price_half_yearly}</span>}
                                      <span>Yearly: ৳{currentPkg.discounted_price_yearly || currentPkg.regular_price_yearly}</span>
                                      {currentPkg.setup_fee > 0 && <span className="text-amber-600">Setup: ৳{currentPkg.setup_fee}</span>}
                                    </div>
                                  )}
                                  {currentPkg?.features?.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {currentPkg.features.slice(0, 4).map((f, i) => (
                                        <span key={i} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{f}</span>
                                      ))}
                                      {currentPkg.features.length > 4 && (
                                        <span className="text-xs text-slate-400">+{currentPkg.features.length - 4} more</span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <p className="text-xs text-slate-400 italic">No package assigned</p>
                              )}
                              <div className="flex items-center gap-2 mt-3 pt-2 border-t border-slate-100">
                                <Select
                                  value={packageChanges[tenant.id] || ''}
                                  onValueChange={(v) => setPackageChanges(p => ({ ...p, [tenant.id]: v }))}
                                >
                                  <SelectTrigger className="h-8 text-xs flex-1">
                                    <SelectValue placeholder="Change package..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {packages.filter(p => p.is_active && p.id !== tenant.subscription_plan).map(pkg => (
                                      <SelectItem key={pkg.id} value={pkg.id}>
                                        {pkg.name} — ৳{pkg.discounted_price_monthly || pkg.regular_price_monthly}/mo
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <Button
                                  size="sm"
                                  className="h-8 bg-indigo-600 hover:bg-indigo-700 text-white text-xs"
                                  disabled={!packageChanges[tenant.id]}
                                  onClick={() => changeTenantPackage(tenant.id)}
                                >
                                  Apply
                                </Button>
                              </div>
                            </div>

                            {/* Owner Credentials Section */}
                            {credentials[tenant.id] && !credentials[tenant.id].loading && credentials[tenant.id].email && (
                              <div className="mt-3 p-3 bg-white border border-slate-200 rounded-lg space-y-2">
                                <div className="flex items-center gap-2 text-sm">
                                  <span className="text-slate-500 w-14">Email:</span>
                                  <span className="font-mono font-medium text-slate-800">{credentials[tenant.id].email}</span>
                                  <button onClick={() => copyToClipboard(credentials[tenant.id].email)} className="p-1 hover:bg-slate-100 rounded" title="Copy">
                                    <Copy className="w-3.5 h-3.5 text-slate-400" />
                                  </button>
                                </div>
                                <div className="flex items-center gap-2 text-sm">
                                  <span className="text-slate-500 w-14">Pass:</span>
                                  {credentials[tenant.id].password ? (
                                    <>
                                      <span className="font-mono font-medium text-slate-800">
                                        {credentials[tenant.id].showPw ? credentials[tenant.id].password : '••••••••••'}
                                      </span>
                                      <button onClick={() => setCredentials(prev => ({ ...prev, [tenant.id]: { ...prev[tenant.id], showPw: !prev[tenant.id].showPw } }))} className="p-1 hover:bg-slate-100 rounded">
                                        {credentials[tenant.id].showPw ? <EyeOff className="w-3.5 h-3.5 text-slate-400" /> : <Eye className="w-3.5 h-3.5 text-slate-400" />}
                                      </button>
                                      <button onClick={() => copyToClipboard(credentials[tenant.id].password)} className="p-1 hover:bg-slate-100 rounded" title="Copy">
                                        <Copy className="w-3.5 h-3.5 text-slate-400" />
                                      </button>
                                    </>
                                  ) : (
                                    <span className="text-slate-400 italic text-xs">Not available (older account)</span>
                                  )}
                                  <button
                                    onClick={() => resetOwnerPassword(tenant.id)}
                                    className="p-1 hover:bg-orange-50 rounded text-orange-500" title="Reset Password"
                                  >
                                    <RotateCw className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </div>
                            )}
                            {credentials[tenant.id]?.loading && (
                              <div className="mt-3"><Spinner size="sm" /></div>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-2 ml-4">
                            <Button size="sm" variant="outline" onClick={() => fetchCredentials(tenant.id)}>
                              <KeyRound className="w-4 h-4 mr-1" />
                              {credentials[tenant.id]?.email ? 'Hide' : 'Credentials'}
                            </Button>
                            <Link to={`/${tenant.slug}`} target="_blank">
                              <Button size="sm" variant="outline">
                                <Eye className="w-4 h-4 mr-1" />
                                View
                              </Button>
                            </Link>
                            <Button 
                              size="sm" 
                              variant={tenant.is_active ? 'destructive' : 'default'}
                              onClick={() => toggleTenantStatus(tenant.id)}
                            >
                              {tenant.is_active ? 'Disable' : 'Enable'}
                            </Button>
                            <div className="flex items-center gap-1">
                              <Select 
                                value={renewMonths[tenant.id] || '1'} 
                                onValueChange={(v) => setRenewMonths(p => ({ ...p, [tenant.id]: v }))}
                              >
                                <SelectTrigger className="h-8 w-[80px] text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {[1,2,3,6,12].map(m => (
                                    <SelectItem key={m} value={String(m)}>{m} mo</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Button 
                                size="sm" 
                                className="bg-emerald-600 hover:bg-emerald-700 gap-1"
                                onClick={() => renewSubscription(tenant.id)}
                              >
                                <RefreshCw className="w-3 h-3" />
                                Renew
                              </Button>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                              onClick={() => setDeleteConfirm({ tenantId: tenant.id, name: tenant.name, input: '' })}
                            >
                              <Trash2 className="w-3.5 h-3.5 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-8">No garages registered yet</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Packages Tab */}
          <TabsContent value="packages">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Packages / Pricing Plans</CardTitle>
                <div className="flex gap-2">
                  {packages.length === 0 && (
                    <Button variant="outline" onClick={seedPackages}>
                      Seed Defaults
                    </Button>
                  )}
                  <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={openCreatePackage}>
                    <Plus className="w-4 h-4 mr-1" />
                    Add Package
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {packages.length > 0 ? (
                  <div className="space-y-4">
                    {packages.map((pkg) => (
                      <div key={pkg.id} className={`p-4 rounded-lg border-2 ${
                        !pkg.is_active ? 'border-slate-200 bg-slate-50 opacity-60' :
                        pkg.is_popular ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 bg-white'
                      }`}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-bold text-lg">{pkg.name}</h3>
                              {pkg.name_bn && <span className="text-slate-500">({pkg.name_bn})</span>}
                              <Badge variant="outline" className="font-mono">{pkg.slug}</Badge>
                              {pkg.is_popular && <Badge className="bg-emerald-600">Popular</Badge>}
                              {!pkg.is_active && <Badge className="bg-slate-400">Inactive</Badge>}
                            </div>
                            <p className="text-sm text-slate-600 mb-3">
                              {pkg.description}
                              {pkg.description_bn && <span className="text-slate-400 ml-2">({pkg.description_bn})</span>}
                            </p>
                            {pkg.tagline && (
                              <p className="text-xs text-slate-500 mb-2 italic">
                                {pkg.tagline}
                                {pkg.tagline_bn && <span className="text-slate-400 ml-2">({pkg.tagline_bn})</span>}
                              </p>
                            )}
                            {pkg.setup_fee > 0 && (
                              <p className="text-xs text-amber-700 mb-3 bg-amber-50 inline-block px-2 py-0.5 rounded">Setup Fee: ৳{pkg.setup_fee.toLocaleString()}</p>
                            )}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                              <div>
                                <p className="text-xs text-slate-500 uppercase font-semibold mb-1">Monthly</p>
                                <div className="flex items-baseline gap-2">
                                  {pkg.discounted_price_monthly ? (
                                    <>
                                      <span className="text-2xl font-bold text-emerald-600">৳{pkg.discounted_price_monthly.toLocaleString()}</span>
                                      <span className="text-sm text-slate-400 line-through">৳{pkg.regular_price_monthly.toLocaleString()}</span>
                                    </>
                                  ) : (
                                    <span className="text-2xl font-bold text-emerald-600">৳{pkg.regular_price_monthly.toLocaleString()}</span>
                                  )}
                                </div>
                              </div>
                              <div>
                                <p className="text-xs text-slate-500 uppercase font-semibold mb-1">Half-Yearly</p>
                                <div className="flex items-baseline gap-2">
                                  {pkg.discounted_price_half_yearly ? (
                                    <>
                                      <span className="text-2xl font-bold text-emerald-600">৳{pkg.discounted_price_half_yearly.toLocaleString()}</span>
                                      <span className="text-sm text-slate-400 line-through">৳{(pkg.regular_price_half_yearly || 0).toLocaleString()}</span>
                                    </>
                                  ) : pkg.regular_price_half_yearly ? (
                                    <span className="text-2xl font-bold text-emerald-600">৳{pkg.regular_price_half_yearly.toLocaleString()}</span>
                                  ) : (
                                    <span className="text-sm text-slate-400">—</span>
                                  )}
                                </div>
                              </div>
                              <div>
                                <p className="text-xs text-slate-500 uppercase font-semibold mb-1">Yearly</p>
                                <div className="flex items-baseline gap-2">
                                  {pkg.discounted_price_yearly ? (
                                    <>
                                      <span className="text-2xl font-bold text-emerald-600">৳{pkg.discounted_price_yearly.toLocaleString()}</span>
                                      <span className="text-sm text-slate-400 line-through">৳{pkg.regular_price_yearly.toLocaleString()}</span>
                                    </>
                                  ) : (
                                    <span className="text-2xl font-bold text-emerald-600">৳{pkg.regular_price_yearly.toLocaleString()}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {pkg.features.map((f, i) => (
                                <Badge key={i} variant="outline" className="text-xs">{f}</Badge>
                              ))}
                            </div>
                          </div>
                          <div className="flex gap-2 ml-4">
                            <Button size="sm" variant="outline" onClick={() => openEditPackage(pkg)}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => deletePackage(pkg.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500 mb-4">No packages defined yet</p>
                    <Button onClick={seedPackages} className="bg-emerald-600 hover:bg-emerald-700">
                      Create Default Packages
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Package Create/Edit Dialog */}
            <Dialog open={showPackageDialog} onOpenChange={setShowPackageDialog}>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingPackage ? 'Edit Package' : 'Create Package'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Name (English) *</Label>
                      <Input value={packageForm.name} onChange={e => setPackageForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Standard" />
                    </div>
                    <div>
                      <Label>Name (Bengali)</Label>
                      <Input value={packageForm.name_bn} onChange={e => setPackageForm(p => ({ ...p, name_bn: e.target.value }))} placeholder="e.g. স্ট্যান্ডার্ড" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Slug *</Label>
                      <Input value={packageForm.slug} onChange={e => setPackageForm(p => ({ ...p, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))} placeholder="e.g. standard" />
                      <p className="text-xs text-slate-400 mt-1">Used internally for plan identification</p>
                    </div>
                    <div>
                      <Label>Sort Order</Label>
                      <Input type="number" value={packageForm.sort_order} onChange={e => setPackageForm(p => ({ ...p, sort_order: e.target.value }))} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Description (English)</Label>
                      <Input value={packageForm.description} onChange={e => setPackageForm(p => ({ ...p, description: e.target.value }))} placeholder="For growing garages" />
                    </div>
                    <div>
                      <Label>Description (Bengali)</Label>
                      <Input value={packageForm.description_bn} onChange={e => setPackageForm(p => ({ ...p, description_bn: e.target.value }))} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Tagline (English)</Label>
                      <Input value={packageForm.tagline} onChange={e => setPackageForm(p => ({ ...p, tagline: e.target.value }))} placeholder="Great for growing garages" />
                    </div>
                    <div>
                      <Label>Tagline (Bengali)</Label>
                      <Input value={packageForm.tagline_bn} onChange={e => setPackageForm(p => ({ ...p, tagline_bn: e.target.value }))} placeholder="বৃদ্ধিশীল গ্যারেজের জন্য" />
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <h4 className="font-semibold mb-3">Monthly Pricing (BDT)</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Regular Price *</Label>
                        <Input type="number" value={packageForm.regular_price_monthly} onChange={e => setPackageForm(p => ({ ...p, regular_price_monthly: e.target.value }))} placeholder="1799" />
                      </div>
                      <div>
                        <Label>Discounted Price</Label>
                        <Input type="number" value={packageForm.discounted_price_monthly} onChange={e => setPackageForm(p => ({ ...p, discounted_price_monthly: e.target.value }))} placeholder="799" />
                      </div>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <h4 className="font-semibold mb-3">Half-Yearly Pricing (BDT)</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Regular Price</Label>
                        <Input type="number" value={packageForm.regular_price_half_yearly} onChange={e => setPackageForm(p => ({ ...p, regular_price_half_yearly: e.target.value }))} placeholder="10794" />
                        <p className="text-xs text-slate-400 mt-1">Auto: Regular Monthly × 6</p>
                      </div>
                      <div>
                        <Label>Discounted Price</Label>
                        <Input type="number" value={packageForm.discounted_price_half_yearly} onChange={e => setPackageForm(p => ({ ...p, discounted_price_half_yearly: e.target.value }))} placeholder="3995" />
                        <p className="text-xs text-slate-400 mt-1">Effectively 5 months (1 free)</p>
                      </div>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <h4 className="font-semibold mb-3">Yearly Pricing (BDT)</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Regular Price *</Label>
                        <Input type="number" value={packageForm.regular_price_yearly} onChange={e => setPackageForm(p => ({ ...p, regular_price_yearly: e.target.value }))} placeholder="21588" />
                      </div>
                      <div>
                        <Label>Discounted Price</Label>
                        <Input type="number" value={packageForm.discounted_price_yearly} onChange={e => setPackageForm(p => ({ ...p, discounted_price_yearly: e.target.value }))} placeholder="7990" />
                        <p className="text-xs text-slate-400 mt-1">Effectively 10 months (2 free)</p>
                      </div>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <h4 className="font-semibold mb-3">Setup Fee (BDT)</h4>
                    <div className="max-w-xs">
                      <Input type="number" value={packageForm.setup_fee} onChange={e => setPackageForm(p => ({ ...p, setup_fee: e.target.value }))} placeholder="2000" />
                      <p className="text-xs text-slate-400 mt-1">One-time fee (waived for yearly subscriptions)</p>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <h4 className="font-semibold mb-3">Features</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Features (English, one per line)</Label>
                        <Textarea rows={4} value={packageForm.features} onChange={e => setPackageForm(p => ({ ...p, features: e.target.value }))} placeholder={"2 locations\n8 users\nUnlimited jobs"} />
                      </div>
                      <div>
                        <Label>Features (Bengali, one per line)</Label>
                        <Textarea rows={4} value={packageForm.features_bn} onChange={e => setPackageForm(p => ({ ...p, features_bn: e.target.value }))} placeholder={"২টি লোকেশন\n৮ জন ব্যবহারকারী"} />
                      </div>
                    </div>
                  </div>

                  <div className="border-t pt-4 flex items-center gap-6">
                    <div className="flex items-center gap-2">
                      <Switch checked={packageForm.is_popular} onCheckedChange={v => setPackageForm(p => ({ ...p, is_popular: v }))} />
                      <Label>Mark as Popular</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch checked={packageForm.is_active} onCheckedChange={v => setPackageForm(p => ({ ...p, is_active: v }))} />
                      <Label>Active</Label>
                    </div>
                  </div>

                  <Button onClick={savePackage} className="w-full bg-emerald-600 hover:bg-emerald-700">
                    {editingPackage ? 'Update Package' : 'Create Package'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Finance Tab */}
          <TabsContent value="finance">
            <SaasFinanceModule language={language} />
          </TabsContent>

          {/* Marketing Tab */}
          <TabsContent value="marketing">
            <div className="space-y-6">
              {/* Platform Settings */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Megaphone className="w-5 h-5" />
                    Platform Marketing Settings
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {platformSettings ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label>Credit Price (BDT per credit)</Label>
                          <Input
                            type="number"
                            step="0.5"
                            min="0"
                            value={platformSettings.credit_price_bdt || ''}
                            onChange={e => setPlatformSettings(prev => ({ ...prev, credit_price_bdt: parseFloat(e.target.value) || 0 }))}
                          />
                        </div>
                        <div className="md:col-span-2">
                          <div className="flex items-center justify-between mb-2">
                            <Label>Top-up Bundles</Label>
                            <div className="flex items-center gap-2">
                              <Button type="button" variant="outline" size="sm" onClick={seedDefaultTopupBundles} disabled={savingPlatform}>
                                Seed Defaults
                              </Button>
                              <Button type="button" variant="outline" size="sm" onClick={addTopupBundleRow}>
                                <Plus className="w-4 h-4 mr-1" /> Add Bundle
                              </Button>
                            </div>
                          </div>
                          <div className="rounded-lg border border-slate-200 overflow-x-auto">
                            <table className="w-full min-w-[720px] text-sm">
                              <thead className="bg-slate-50 text-slate-600">
                                <tr>
                                  <th className="text-left p-2">Name</th>
                                  <th className="text-left p-2">Credits</th>
                                  <th className="text-left p-2">Price (BDT)</th>
                                  <th className="text-left p-2">Sort</th>
                                  <th className="text-left p-2">Active</th>
                                  <th className="text-right p-2">Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {topupBundles.length === 0 ? (
                                  <tr>
                                    <td className="p-3 text-slate-400" colSpan={6}>No bundles configured</td>
                                  </tr>
                                ) : topupBundles.map((bundle, idx) => (
                                  <tr key={bundle.id || idx} className="border-t border-slate-100">
                                    <td className="p-2">
                                      <Input value={bundle.name || ''} onChange={e => updateTopupBundle(idx, 'name', e.target.value)} placeholder="Bundle name" />
                                    </td>
                                    <td className="p-2">
                                      <Input type="number" min="1" value={bundle.credits ?? ''} onChange={e => updateTopupBundle(idx, 'credits', parseInt(e.target.value) || 0)} />
                                    </td>
                                    <td className="p-2">
                                      <Input type="number" min="0" step="0.01" value={bundle.price_bdt ?? ''} onChange={e => updateTopupBundle(idx, 'price_bdt', parseFloat(e.target.value) || 0)} />
                                    </td>
                                    <td className="p-2">
                                      <Input type="number" min="0" value={bundle.sort_order ?? idx + 1} onChange={e => updateTopupBundle(idx, 'sort_order', parseInt(e.target.value) || 0)} />
                                    </td>
                                    <td className="p-2">
                                      <Switch checked={bundle.is_active !== false} onCheckedChange={v => updateTopupBundle(idx, 'is_active', v)} />
                                    </td>
                                    <td className="p-2 text-right">
                                      <Button type="button" size="sm" variant="destructive" onClick={() => removeTopupBundleRow(idx)}>
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          <p className="text-xs text-slate-500 mt-1">Bundles appear in tenant credits top-up dialog in sort order.</p>
                        </div>

                        {/* SMS Quick Templates CRUD */}
                        <div className="md:col-span-2">
                          <div className="flex items-center justify-between mb-2">
                            <Label>SMS Quick Templates</Label>
                            <Button type="button" variant="outline" size="sm" onClick={addSmsTemplate}>
                              <Plus className="w-4 h-4 mr-1" /> Add Template
                            </Button>
                          </div>
                          <div className="rounded-lg border border-slate-200 overflow-x-auto">
                            <table className="w-full min-w-[600px] text-sm">
                              <thead className="bg-slate-50 text-slate-600">
                                <tr>
                                  <th className="text-left p-2">Name</th>
                                  <th className="text-left p-2 w-36">Category</th>
                                  <th className="text-left p-2">Message Body</th>
                                  <th className="text-left p-2 w-16">Active</th>
                                  <th className="text-right p-2 w-16">Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {smsTemplates.length === 0 ? (
                                  <tr>
                                    <td className="p-3 text-slate-400" colSpan={5}>No templates configured. Garages will see default hardcoded templates.</td>
                                  </tr>
                                ) : smsTemplates.map((tmpl, idx) => (
                                  <tr key={tmpl.id || idx} className="border-t border-slate-100">
                                    <td className="p-2">
                                      <Input value={tmpl.name || ''} onChange={e => updateSmsTemplate(idx, 'name', e.target.value)} placeholder="Template name" />
                                    </td>
                                    <td className="p-2">
                                      <Select value={tmpl.category || 'General'} onValueChange={v => updateSmsTemplate(idx, 'category', v)}>
                                        <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                          {SMS_TEMPLATE_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                        </SelectContent>
                                      </Select>
                                    </td>
                                    <td className="p-2">
                                      <Textarea value={tmpl.body || ''} onChange={e => updateSmsTemplate(idx, 'body', e.target.value)} placeholder="Hello {customer_name}, ..." rows={2} className="min-h-[60px] text-sm" />
                                    </td>
                                    <td className="p-2">
                                      <Switch checked={tmpl.is_active !== false} onCheckedChange={v => updateSmsTemplate(idx, 'is_active', v)} />
                                    </td>
                                    <td className="p-2 text-right">
                                      <Button type="button" size="sm" variant="destructive" onClick={() => removeSmsTemplate(idx)}>
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          <p className="text-xs text-slate-500 mt-1">
                            Templates appear in garage SMS campaign creation. Use placeholders: {'{customer_name}'}, {'{garage_name}'}, {'{garage_phone}'}, {'{garage_address}'}, {'{garage_website}'}
                          </p>
                        </div>

                        <div>
                          <Label>SMS Provider</Label>
                          <Select
                            value={platformSettings.sms_provider || 'stub'}
                            onValueChange={val => setPlatformSettings(prev => ({ ...prev, sms_provider: val }))}
                          >
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="stub">Stub (Simulated)</SelectItem>
                              <SelectItem value="sms.net.bd">sms.net.bd (Bangladesh)</SelectItem>
                              <SelectItem value="twilio">Twilio</SelectItem>
                              <SelectItem value="vonage">Vonage</SelectItem>
                              <SelectItem value="local">Local Provider</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>SMS Sender ID</Label>
                          <Input
                            value={platformSettings.sms_sender_id || ''}
                            onChange={e => setPlatformSettings(prev => ({ ...prev, sms_sender_id: e.target.value }))}
                          />
                        </div>
                        <div>
                          <Label>SMS API Key</Label>
                          <Input
                            type="password"
                            value={platformSettings.sms_api_key || ''}
                            onChange={e => setPlatformSettings(prev => ({ ...prev, sms_api_key: e.target.value }))}
                          />
                        </div>
                        <div>
                          <Label>SMS API URL</Label>
                          <Input
                            value={platformSettings.sms_api_url || ''}
                            onChange={e => setPlatformSettings(prev => ({ ...prev, sms_api_url: e.target.value }))}
                          />
                        </div>
                        <div>
                          <Label>Founder Notification Phones</Label>
                          <Input
                            value={platformSettings.notification_phones || ''}
                            onChange={e => setPlatformSettings(prev => ({ ...prev, notification_phones: e.target.value }))}
                            placeholder="+8801786264886,+8801877425300"
                          />
                          <p className="text-xs text-slate-500 mt-1">Comma-separated. These numbers get SMS when a new subscription request comes in.</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={platformSettings.is_sms_enabled || false}
                          onCheckedChange={val => setPlatformSettings(prev => ({ ...prev, is_sms_enabled: val }))}
                        />
                        <Label>Enable SMS Sending</Label>
                      </div>
                      <Button
                        disabled={savingPlatform}
                        onClick={async () => {
                          setSavingPlatform(true);
                          try {
                            const parsedBundles = sanitizeTopupBundles(topupBundles);

                            const { sms_provider, sms_api_key, sms_api_url, sms_sender_id, credit_price_bdt, is_sms_enabled, notification_phones } = platformSettings;
                            const res = await axios.put(`${API}/admin/platform-settings`, {
                              sms_provider, sms_api_key, sms_api_url, sms_sender_id,
                              credit_price_bdt, is_sms_enabled, notification_phones,
                              topup_bundles: parsedBundles,
                              sms_templates: smsTemplates.filter(t => t.name && t.body),
                            });
                            setPlatformSettings(res.data);
                            setTopupBundles(Array.isArray(res.data?.topup_bundles) ? res.data.topup_bundles : parsedBundles);
                            setSmsTemplates(Array.isArray(res.data?.sms_templates) ? res.data.sms_templates : smsTemplates);
                            toast({ title: 'Settings saved' });
                          } catch (err) {
                            toast({ title: 'Failed to save settings', variant: 'destructive' });
                          } finally {
                            setSavingPlatform(false);
                          }
                        }}
                      >
                        {savingPlatform ? 'Saving...' : 'Save Settings'}
                      </Button>
                    </div>
                  ) : (
                    <div className="flex justify-center py-6"><Spinner /></div>
                  )}
                </CardContent>
              </Card>

              {/* Credit Purchase Requests */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5" />
                    Pending Credit Purchase Requests
                    {topupRequests.length > 0 && (
                      <Badge className="bg-orange-500">{topupRequests.length}</Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {topupRequests.length > 0 ? (
                    <div className="space-y-4">
                      {topupRequests.map(req => (
                        <div key={req.id} className="p-4 rounded-lg border-2 border-orange-300 bg-orange-50">
                          <div className="flex items-start justify-between">
                            <div>
                              <h3 className="font-bold">{req.tenant_name}</h3>
                              <p className="text-slate-600">
                                {req.amount_credits} credits • ৳{req.amount_bdt} • {req.payment_method}
                              </p>
                              {req.transaction_id && (
                                <p className="text-xs text-slate-500">TxnID: {req.transaction_id}</p>
                              )}
                              {req.payment_phone && (
                                <p className="text-xs text-slate-500">Phone: {req.payment_phone}</p>
                              )}
                              <p className="text-xs text-slate-400 mt-1">
                                {formatDate(req.created_at, language)}
                              </p>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700"
                                onClick={async () => {
                                  try {
                                    await axios.put(`${API}/admin/credit-topup-requests/${req.id}/approve`);
                                    toast({ title: `Approved ${req.amount_credits} credits for ${req.tenant_name}` });
                                    setTopupRequests(prev => prev.filter(r => r.id !== req.id));
                                  } catch (err) {
                                    toast({ title: 'Failed to approve', variant: 'destructive' });
                                  }
                                }}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" /> Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={async () => {
                                  try {
                                    await axios.put(`${API}/admin/credit-topup-requests/${req.id}/reject`, { reason: 'Payment not verified' });
                                    toast({ title: 'Request rejected' });
                                    setTopupRequests(prev => prev.filter(r => r.id !== req.id));
                                  } catch (err) {
                                    toast({ title: 'Failed to reject', variant: 'destructive' });
                                  }
                                }}
                              >
                                <XCircle className="w-4 h-4 mr-1" /> Reject
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500 text-center py-8">No pending credit purchase requests</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Demo Requests Tab */}
          <TabsContent value="demos">
            <Card>
              <CardHeader>
                <CardTitle>Demo Requests</CardTitle>
              </CardHeader>
              <CardContent>
                {demoRequests.length > 0 ? (
                  <div className="space-y-4">
                    {demoRequests.map((demo) => (
                      <div 
                        key={demo.id} 
                        className={`p-4 rounded-lg border-2 ${
                          demo.status === 'pending' ? 'border-orange-300 bg-orange-50' :
                          demo.status === 'contacted' ? 'border-blue-300 bg-blue-50' :
                          demo.status === 'completed' ? 'border-green-300 bg-green-50' :
                          'border-slate-300 bg-slate-50'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-bold">{demo.garage_name}</h3>
                              <Badge className={
                                demo.status === 'pending' ? 'bg-orange-500' :
                                demo.status === 'contacted' ? 'bg-blue-500' :
                                demo.status === 'completed' ? 'bg-green-500' : 'bg-slate-500'
                              }>
                                {demo.status}
                              </Badge>
                            </div>
                            <p className="text-slate-600">{demo.contact_name}</p>
                            <p className="text-slate-600">{demo.email} • {demo.phone}</p>
                            {demo.message && (
                              <p className="mt-2 text-slate-600 italic">"{demo.message}"</p>
                            )}
                            <p className="text-xs text-slate-400 mt-2">
                              {formatDate(demo.created_at, language)}
                            </p>
                          </div>

                          {demo.status === 'pending' && (
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                className="bg-blue-600 hover:bg-blue-700"
                                onClick={() => updateDemoStatus(demo.id, 'contacted')}
                              >
                                Mark Contacted
                              </Button>
                              <Button 
                                size="sm" 
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => updateDemoStatus(demo.id, 'completed')}
                              >
                                Complete
                              </Button>
                            </div>
                          )}

                          {demo.status === 'contacted' && (
                            <Button 
                              size="sm" 
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => updateDemoStatus(demo.id, 'completed')}
                            >
                              Mark Complete
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-8">No demo requests yet</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Consumable Categories Tab */}
          <TabsContent value="consumables">
            <div className="space-y-6">
              {/* Header with actions */}
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold">System Consumable Categories</h2>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={fetchUnmatchedReport}>
                    <Search className="w-4 h-4 mr-2" /> Unmatched Report
                  </Button>
                  <Button variant="outline" onClick={syncToTenants}>
                    <RefreshCw className="w-4 h-4 mr-2" /> Sync to All Tenants
                  </Button>
                  <Button className="bg-blue-800 hover:bg-blue-900" onClick={openCatCreate}>
                    <Plus className="w-4 h-4 mr-2" /> Add Category
                  </Button>
                </div>
              </div>

              {/* Unmatched report */}
              {unmatchedReport && (
                <Card className="border-orange-300 bg-orange-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-orange-800 flex items-center justify-between">
                      Unmatched Parts Report
                      <Button size="sm" variant="ghost" onClick={() => setUnmatchedReport(null)}>
                        <XCircle className="w-4 h-4" />
                      </Button>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {unmatchedReport.tenants?.length > 0 ? (
                      <div className="space-y-3">
                        {unmatchedReport.tenants.map((t, i) => (
                          <div key={i} className="p-3 bg-white rounded-lg border">
                            <p className="font-semibold">{t.tenant_name} <span className="text-sm text-slate-500">({t.unmapped_count} unmapped)</span></p>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {t.sample_parts?.slice(0, 10).map((p, j) => (
                                <Badge key={j} variant="outline" className="text-xs">{p}</Badge>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-slate-600">All tenant parts are mapped!</p>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Pending Template Approvals */}
              {pendingTemplates.length > 0 && (
                <Card className="border-amber-300 bg-amber-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-amber-800 flex items-center gap-2">
                      <Clock className="w-5 h-5" />
                      Pending Template Approvals ({pendingTemplates.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {pendingTemplates.map((pt) => (
                      <div key={`${pt.id}-${pt.tenant_id}`} className="p-3 bg-white rounded-lg border border-amber-200">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <p className="font-semibold text-sm">{pt.name} <span className="text-xs text-slate-500">— {pt.tenant_name}</span></p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 text-white h-8 text-xs"
                              onClick={() => handleTemplateApproval(pt.id, pt.tenant_id, 'approve')}
                              disabled={approvingTemplate === `${pt.id}-approve`}
                            >
                              <CheckCircle className="w-3.5 h-3.5 mr-1" /> Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              className="h-8 text-xs"
                              onClick={() => handleTemplateApproval(pt.id, pt.tenant_id, 'reject')}
                              disabled={approvingTemplate === `${pt.id}-reject`}
                            >
                              <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                            </Button>
                          </div>
                        </div>
                        {pt.message_template && (
                          <div className="p-2 bg-slate-50 rounded border text-sm text-slate-700 whitespace-pre-wrap">
                            {pt.message_template}
                          </div>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Categories list */}
              {catLoading ? (
                <div className="flex justify-center py-12"><Spinner /></div>
              ) : (
                <div className="space-y-3">
                  {sysCategories.map((cat) => (
                    <Card key={cat.id} className={cat.is_active === false ? 'opacity-60' : ''}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div
                            className="flex items-center gap-3 cursor-pointer flex-1"
                            onClick={() => setExpandedCat(expandedCat === cat.id ? null : cat.id)}
                          >
                            <Droplet className="w-5 h-5 text-blue-800" />
                            <div>
                              <p className="font-semibold">{cat.name}</p>
                              <p className="text-sm text-slate-500">
                                Interval: {cat.default_interval_days} days • Keywords: {(cat.keywords || []).length}
                                {cat.is_active === false && ' • Disabled'}
                              </p>
                            </div>
                            {expandedCat === cat.id ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => openCatEdit(cat)}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            {!cat.is_system && (
                              <Button size="sm" variant="destructive" onClick={() => deleteCat(cat.id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                        {expandedCat === cat.id && (
                          <div className="mt-3 pt-3 border-t">
                            <p className="text-sm font-medium mb-1">Keywords:</p>
                            <div className="flex flex-wrap gap-1">
                              {(cat.keywords || []).map((kw, i) => (
                                <Badge key={i} variant="outline" className="text-xs">{kw}</Badge>
                              ))}
                              {(cat.keywords || []).length === 0 && <p className="text-sm text-slate-400">No keywords</p>}
                            </div>
                            {cat.message_template && (
                              <div className="mt-2">
                                <p className="text-sm font-medium mb-1">Message Template:</p>
                                <SuperAdminPreview template={cat.message_template} />
                              </div>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                  {sysCategories.length === 0 && (
                    <p className="text-slate-500 text-center py-12">No system categories. Create one or check seeding.</p>
                  )}
                </div>
              )}
            </div>

            {/* Create/Edit Category Dialog */}
            <Dialog open={showCatDialog} onOpenChange={setShowCatDialog}>
              <DialogContent className="max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingCat ? 'Edit Category' : 'New System Category'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-2">
                  <div>
                    <Label>Name</Label>
                    <Input value={catForm.name} onChange={e => setCatForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Oils & Lubricants" />
                  </div>
                  <div>
                    <Label>Default Interval (days)</Label>
                    <Input type="number" min="1" value={catForm.default_interval_days} onChange={e => setCatForm(f => ({ ...f, default_interval_days: e.target.value }))} />
                  </div>
                  <div>
                    <Label>Keywords (comma-separated)</Label>
                    <Textarea value={catForm.keywords} onChange={e => setCatForm(f => ({ ...f, keywords: e.target.value }))} placeholder="oil, mobil, engine oil, 20w40, ..." rows={3} />
                  </div>
                  <div>
                    <Label>Message Template (optional)</Label>
                    <Textarea value={catForm.message_template} onChange={e => setCatForm(f => ({ ...f, message_template: e.target.value }))} placeholder="Write your message using the chips below..." rows={4} />
                    <div>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {REMINDER_CHIPS.map(({ token, label }) => (
                          <button
                            key={token}
                            type="button"
                            onClick={() => setCatForm(f => ({ ...f, message_template: `${f.message_template}${f.message_template ? ' ' : ''}${token}` }))}
                            className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] rounded-full bg-slate-50 text-slate-600 border border-slate-200 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 transition-colors"
                            title={token}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                      <p className="text-[10px] text-muted-foreground mt-1">Tap a chip to insert into your message</p>
                    </div>
                    <SuperAdminPreview template={catForm.message_template} />
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={catForm.is_active} onCheckedChange={v => setCatForm(f => ({ ...f, is_active: v }))} />
                    <Label>Active</Label>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setShowCatDialog(false)}>Cancel</Button>
                    <Button className="bg-blue-800 hover:bg-blue-900" onClick={saveCat}>{editingCat ? 'Update' : 'Create'}</Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* AI & Suppliers Tab */}
          <TabsContent value="ai-suppliers">
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-blue-600" /> AI Import & Suppliers
                </h2>
                <Button variant="outline" onClick={() => { fetchAiStats(); fetchSuppliers(); }}>
                  <RefreshCw className="w-4 h-4 mr-2" /> Refresh
                </Button>
              </div>

              {/* Sub-tab navigation */}
              <div className="flex gap-2 border-b pb-2">
                {[
                  { key: 'overview', label: 'Usage Overview' },
                  { key: 'suppliers', label: 'Suppliers' },
                  { key: 'batches', label: 'Recent Imports' },
                  { key: 'sms-costs', label: 'SMS AI Costs' },
                ].map(tab => (
                  <button
                    key={tab.key}
                    className={`px-3 py-1.5 text-sm rounded-md transition-colors ${aiSubTab === tab.key ? 'bg-blue-100 text-blue-800 font-medium' : 'text-slate-500 hover:bg-slate-100'}`}
                    onClick={() => setAiSubTab(tab.key)}
                  >{tab.label}</button>
                ))}
              </div>

              {aiStatsLoading ? (
                <div className="flex justify-center py-12"><Spinner /></div>
              ) : aiSubTab === 'sms-costs' ? (
                aiSmsLoading ? (
                  <div className="flex justify-center py-12"><Spinner /></div>
                ) : !aiSmsStats ? (
                  <p className="text-slate-500 text-center py-12">No AI SMS usage data yet.</p>
                ) : (
                  <div className="space-y-6">
                    {/* Top stat cards */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      <Card><CardContent className="p-3">
                        <p className="text-[10px] uppercase tracking-wider text-slate-500">Total Calls</p>
                        <p className="text-xl font-bold mt-1">{aiSmsStats.totals?.calls?.toLocaleString() || 0}</p>
                      </CardContent></Card>
                      <Card><CardContent className="p-3">
                        <p className="text-[10px] uppercase tracking-wider text-slate-500">Total Tokens</p>
                        <p className="text-xl font-bold mt-1">{aiSmsStats.totals?.total_tokens?.toLocaleString() || 0}</p>
                      </CardContent></Card>
                      <Card><CardContent className="p-3">
                        <p className="text-[10px] uppercase tracking-wider text-slate-500">Cost (BDT)</p>
                        <p className="text-xl font-bold mt-1 text-emerald-700">৳{(aiSmsStats.totals?.cost_bdt || 0).toFixed(2)}</p>
                        <p className="text-[9px] text-slate-400">${(aiSmsStats.totals?.cost_usd || 0).toFixed(4)} USD</p>
                      </CardContent></Card>
                      <Card><CardContent className="p-3">
                        <p className="text-[10px] uppercase tracking-wider text-slate-500">Rate</p>
                        <p className="text-xl font-bold mt-1">125</p>
                        <p className="text-[9px] text-slate-400">BDT per USD</p>
                      </CardContent></Card>
                      <Card><CardContent className="p-3">
                        <p className="text-[10px] uppercase tracking-wider text-slate-500">Limits</p>
                        <p className="text-xs mt-1 font-medium">{aiSmsStats.limits?.daily}/day</p>
                        <p className="text-xs text-slate-500">{aiSmsStats.limits?.monthly}/month</p>
                      </CardContent></Card>
                    </div>

                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-semibold">Per Tenant</h3>
                      <Button size="sm" variant="outline" onClick={fetchAiSmsStats}><RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh</Button>
                    </div>

                    <div className="overflow-x-auto rounded-lg border">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-50 text-slate-600">
                          <tr>
                            <th className="text-left px-3 py-2">Tenant</th>
                            <th className="text-right px-3 py-2">Calls</th>
                            <th className="text-right px-3 py-2">In Tokens</th>
                            <th className="text-right px-3 py-2">Out Tokens</th>
                            <th className="text-right px-3 py-2">Cost (BDT)</th>
                            <th className="text-right px-3 py-2">Last Call</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(aiSmsStats.per_tenant || []).length === 0 ? (
                            <tr><td colSpan={6} className="text-center text-slate-400 py-6">No tenant usage yet</td></tr>
                          ) : aiSmsStats.per_tenant.map((t) => (
                            <tr key={t.tenant_id} className="border-t hover:bg-slate-50">
                              <td className="px-3 py-2 font-medium">{t.tenant_name || t.tenant_id}</td>
                              <td className="px-3 py-2 text-right">{t.calls}</td>
                              <td className="px-3 py-2 text-right">{(t.input_tokens || 0).toLocaleString()}</td>
                              <td className="px-3 py-2 text-right">{(t.output_tokens || 0).toLocaleString()}</td>
                              <td className="px-3 py-2 text-right font-mono text-emerald-700">৳{((t.cost_usd || 0) * 125).toFixed(2)}</td>
                              <td className="px-3 py-2 text-right text-slate-500">{t.last_call ? new Date(t.last_call).toLocaleDateString() : '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {(aiSmsStats.model_distribution || []).length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold mb-2">By Model</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                          {aiSmsStats.model_distribution.map((m) => (
                            <Card key={m.model}><CardContent className="p-3">
                              <p className="text-xs font-mono text-slate-700">{m.model}</p>
                              <p className="text-[11px] text-slate-500 mt-1">{m.calls} calls · {(m.tokens || 0).toLocaleString()} tokens</p>
                              <p className="text-xs text-emerald-700 font-medium">৳{((m.cost_usd || 0) * 125).toFixed(2)}</p>
                            </CardContent></Card>
                          ))}
                        </div>
                      </div>
                    )}

                    {(aiSmsStats.recent || []).length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold mb-2">Recent Generations</h3>
                        <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
                          {aiSmsStats.recent.map((r, i) => (
                            <Card key={i}><CardContent className="p-2.5">
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0 flex-1">
                                  <p className="text-[11px] text-slate-500 truncate"><strong className="text-slate-700">{r.tenant_name}</strong> · {r.language} · {r.segments} seg</p>
                                  <p className="text-xs text-slate-700 truncate mt-0.5" title={r.prompt}>📝 {r.prompt}</p>
                                  <p className="text-xs text-violet-700 truncate mt-0.5" title={r.message}>✨ {r.message}</p>
                                </div>
                                <div className="text-right shrink-0">
                                  <p className="text-[11px] text-emerald-700 font-mono">৳{((r.cost_usd || 0) * 125).toFixed(3)}</p>
                                  <p className="text-[10px] text-slate-400">{r.created_at ? new Date(r.created_at).toLocaleString() : ''}</p>
                                </div>
                              </div>
                            </CardContent></Card>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )
              ) : aiStats ? (
                <>
                  {/* Usage Overview */}
                  {aiSubTab === 'overview' && (
                    <div className="space-y-6">
                      {/* Stats Cards */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <Card>
                          <CardContent className="p-4 text-center">
                            <FileText className="w-6 h-6 text-blue-500 mx-auto mb-1" />
                            <p className="text-2xl font-bold">{aiStats.overview?.total_batches || 0}</p>
                            <p className="text-xs text-slate-500">Total Scans</p>
                          </CardContent>
                        </Card>
                        <Card>
                          <CardContent className="p-4 text-center">
                            <Package className="w-6 h-6 text-green-500 mx-auto mb-1" />
                            <p className="text-2xl font-bold">{aiStats.overview?.total_approved || 0}</p>
                            <p className="text-xs text-slate-500">Parts Imported</p>
                          </CardContent>
                        </Card>
                        <Card>
                          <CardContent className="p-4 text-center">
                            <Sparkles className="w-6 h-6 text-purple-500 mx-auto mb-1" />
                            <p className="text-2xl font-bold">{aiStats.overview?.total_llm_calls || 0}</p>
                            <p className="text-xs text-slate-500">Gemini API Calls</p>
                          </CardContent>
                        </Card>
                        <Card>
                          <CardContent className="p-4 text-center">
                            <DollarSign className="w-6 h-6 text-orange-500 mx-auto mb-1" />
                            <p className="text-2xl font-bold">{aiStats.overview?.total_credits_charged || 0}</p>
                            <p className="text-xs text-slate-500">Credits Charged</p>
                          </CardContent>
                        </Card>
                      </div>

                      {/* Token Usage */}
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-base">LLM Token Usage</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-3 gap-4">
                            <div className="text-center p-3 bg-blue-50 rounded-lg">
                              <p className="text-xl font-bold text-blue-700">{(aiStats.overview?.total_input_tokens || 0).toLocaleString()}</p>
                              <p className="text-xs text-slate-500">Input Tokens</p>
                            </div>
                            <div className="text-center p-3 bg-green-50 rounded-lg">
                              <p className="text-xl font-bold text-green-700">{(aiStats.overview?.total_output_tokens || 0).toLocaleString()}</p>
                              <p className="text-xs text-slate-500">Output Tokens</p>
                            </div>
                            <div className="text-center p-3 bg-purple-50 rounded-lg">
                              <p className="text-xl font-bold text-purple-700">{(aiStats.overview?.total_tokens || 0).toLocaleString()}</p>
                              <p className="text-xs text-slate-500">Total Tokens</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Model Distribution */}
                      {aiStats.model_distribution?.length > 0 && (
                        <Card>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-base">Model Distribution</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-2">
                              {aiStats.model_distribution.map((m, i) => (
                                <div key={i} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                                  <span className="text-sm font-mono">{m.model}</span>
                                  <div className="flex items-center gap-3">
                                    <span className="text-sm text-slate-600">{m.count} calls</span>
                                    <span className="text-xs text-slate-400">{(m.total_tokens || 0).toLocaleString()} tokens</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {/* Per-tenant Breakdown */}
                      {aiStats.per_tenant?.length > 0 && (
                        <Card>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-base">Usage by Garage</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="border-b text-left">
                                    <th className="py-2 pr-4 font-medium">Garage</th>
                                    <th className="py-2 pr-4 font-medium text-right">Scans</th>
                                    <th className="py-2 pr-4 font-medium text-right">Parts</th>
                                    <th className="py-2 pr-4 font-medium text-right">API Calls</th>
                                    <th className="py-2 pr-4 font-medium text-right">Tokens</th>
                                    <th className="py-2 font-medium text-right">Credits</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {aiStats.per_tenant.map((t, i) => (
                                    <tr key={i} className="border-b last:border-0">
                                      <td className="py-2 pr-4">
                                        <p className="font-medium">{t.tenant_name}</p>
                                        <p className="text-xs text-slate-400">/{t.tenant_slug}</p>
                                      </td>
                                      <td className="py-2 pr-4 text-right">{t.batches}</td>
                                      <td className="py-2 pr-4 text-right">{t.approved}</td>
                                      <td className="py-2 pr-4 text-right">{t.llm_calls}</td>
                                      <td className="py-2 pr-4 text-right text-xs">{((t.input_tokens || 0) + (t.output_tokens || 0)).toLocaleString()}</td>
                                      <td className="py-2 text-right font-medium">{t.credits_charged}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {/* Category Suggestion (Gemini) Usage */}
                      {aiStats.category_suggest && (
                        <Card className="border-amber-200">
                          <CardHeader className="pb-2">
                            <CardTitle className="text-base flex items-center gap-2">
                              <Sparkles className="w-4 h-4 text-amber-500" />
                              Category Suggestion (Gemini) Usage
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="mb-3 p-3 bg-amber-50 rounded-lg text-center">
                              <p className="text-2xl font-bold text-amber-700">{aiStats.category_suggest.total_gemini_calls || 0}</p>
                              <p className="text-xs text-slate-500">Total Gemini Category Calls (keyword misses only)</p>
                            </div>
                            <div className="grid grid-cols-2 gap-3 mb-3">
                              <div className="p-2.5 bg-slate-50 rounded-lg text-center">
                                <p className="text-lg font-bold text-slate-700">{(aiStats.category_suggest.total_input_tokens || 0).toLocaleString()}</p>
                                <p className="text-[11px] text-slate-400">Input Tokens</p>
                              </div>
                              <div className="p-2.5 bg-slate-50 rounded-lg text-center">
                                <p className="text-lg font-bold text-slate-700">{(aiStats.category_suggest.total_output_tokens || 0).toLocaleString()}</p>
                                <p className="text-[11px] text-slate-400">Output Tokens</p>
                              </div>
                            </div>
                            <div className="p-2.5 bg-green-50 rounded-lg text-center mb-3">
                              <p className="text-sm font-semibold text-green-700">
                                ≈ ${aiStats.category_suggest.estimated_cost_usd?.toFixed(6) || '0.000000'} 
                                <span className="text-slate-400 mx-1">|</span>
                                ৳{((aiStats.category_suggest.estimated_cost_bdt) || 0).toFixed(2)}
                              </p>
                              <p className="text-[11px] text-slate-400">Est. Cost (Flash Lite: $0.075/1M in, $0.30/1M out)</p>
                            </div>
                            {aiStats.category_suggest.per_tenant?.length > 0 && (
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="border-b text-left">
                                      <th className="py-2 pr-4 font-medium">Garage</th>
                                      <th className="py-2 pr-4 font-medium text-right">Calls</th>
                                      <th className="py-2 pr-4 font-medium text-right">Tokens (in/out)</th>
                                      <th className="py-2 font-medium text-right">Last Used</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {aiStats.category_suggest.per_tenant.map((t, i) => (
                                      <tr key={i} className="border-b last:border-0">
                                        <td className="py-2 pr-4">
                                          <p className="font-medium">{t.tenant_name}</p>
                                          <p className="text-xs text-slate-400">/{t.tenant_slug}</p>
                                        </td>
                                        <td className="py-2 pr-4 text-right font-semibold text-amber-700">{t.calls}</td>
                                        <td className="py-2 pr-4 text-right text-xs text-slate-500">{(t.input_tokens || 0).toLocaleString()} / {(t.output_tokens || 0).toLocaleString()}</td>
                                        <td className="py-2 text-right text-xs text-slate-400">{t.last_call ? new Date(t.last_call).toLocaleDateString() : '—'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  )}

                  {/* Suppliers List */}
                  {aiSubTab === 'suppliers' && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-slate-500">{supplierList.length} suppliers across all garages</p>
                      </div>
                      {supplierLoading ? (
                        <div className="flex justify-center py-8"><Spinner /></div>
                      ) : supplierList.length === 0 ? (
                        <p className="text-slate-500 text-center py-12">No suppliers extracted yet. Suppliers are auto-detected from AI imports.</p>
                      ) : (
                        <div className="space-y-2">
                          {supplierList.map((sup, i) => (
                            <Card key={i}>
                              <CardContent className="p-4 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                    <Truck className="w-5 h-5 text-blue-600" />
                                  </div>
                                  <div>
                                    <p className="font-semibold">{sup.name}</p>
                                    <div className="flex items-center gap-3 text-xs text-slate-500">
                                      {sup.phone && <span>{sup.phone}</span>}
                                      {sup.address && <span className="break-words">{sup.address}</span>}
                                    </div>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className="text-sm font-medium">{sup.import_count || 0} imports</p>
                                  {sup.last_seen_at && (
                                    <p className="text-xs text-slate-400">{new Date(sup.last_seen_at).toLocaleDateString()}</p>
                                  )}
                                  {sup.bill_no && (
                                    <p className="text-xs text-slate-400">Last bill: #{sup.bill_no}</p>
                                  )}
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Recent Batches */}
                  {aiSubTab === 'batches' && (
                    <div className="space-y-2">
                      {(aiStats.recent_batches || []).length === 0 ? (
                        <p className="text-slate-500 text-center py-12">No import batches yet.</p>
                      ) : (
                        (aiStats.recent_batches || []).map((b, i) => (
                          <Card key={i}>
                            <CardContent className="p-3 flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <FileText className="w-5 h-5 text-slate-400 shrink-0" />
                                <div>
                                  <p className="text-sm font-medium truncate max-w-[200px]">{b.file_name}</p>
                                  <div className="flex items-center gap-2 text-xs text-slate-500">
                                    <span>{b.tenant_name}</span>
                                    <Badge className={`text-[10px] ${b.status === 'completed' ? 'bg-green-100 text-green-700' : b.status === 'ready_for_review' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}`}>
                                      {b.status}
                                    </Badge>
                                  </div>
                                </div>
                              </div>
                              <div className="text-right text-xs">
                                <div className="flex items-center gap-2">
                                  <span className="text-green-600">{b.approved_count || 0} approved</span>
                                  <span className="text-slate-400">/ {b.total_items || 0}</span>
                                </div>
                                {b.llm_usage && (
                                  <p className="text-slate-400 mt-0.5">{b.llm_usage.total_tokens?.toLocaleString() || 0} tokens</p>
                                )}
                                <p className="text-slate-300">{b.created_at ? new Date(b.created_at).toLocaleDateString() : ''}</p>
                              </div>
                            </CardContent>
                          </Card>
                        ))
                      )}
                    </div>
                  )}
                </>
              ) : (
                <p className="text-slate-500 text-center py-12">Failed to load AI usage data.</p>
              )}
            </div>
          </TabsContent>

          {/* Landing Page Stats Tab */}
          <TabsContent value="landing-page">
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Globe className="w-5 h-5 text-emerald-600" /> Landing Page Stats
                </h2>
                <Button onClick={saveLandingStats} disabled={savingLandingStats} className="bg-emerald-600 hover:bg-emerald-700">
                  {savingLandingStats ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                  Save Changes
                </Button>
              </div>

              {/* Mode Toggle */}
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-bold text-lg">Data Source</p>
                      <p className="text-sm text-slate-500">Choose whether to show real-time data from the database or custom values set below.</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-sm font-medium ${landingStatsMode === 'real' ? 'text-emerald-600' : 'text-slate-400'}`}>Real Data</span>
                      <Switch
                        checked={landingStatsMode === 'custom'}
                        onCheckedChange={(v) => setLandingStatsMode(v ? 'custom' : 'real')}
                      />
                      <span className={`text-sm font-medium ${landingStatsMode === 'custom' ? 'text-blue-600' : 'text-slate-400'}`}>Custom</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {landingStatsMode === 'custom' && (
                <>
                  {/* Hero Section Stats */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Hero Section Stats</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <Label className="text-xs text-slate-500">Active Garages</Label>
                          <Input type="number" value={landingHeroStats.active_garages} onChange={e => setLandingHeroStats(p => ({ ...p, active_garages: e.target.value }))} placeholder="e.g. 10" />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Starting Price (৳)</Label>
                          <Input type="number" value={landingHeroStats.starting_price} onChange={e => setLandingHeroStats(p => ({ ...p, starting_price: e.target.value }))} placeholder="e.g. 1800" />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Support</Label>
                          <Input value={landingHeroStats.support} onChange={e => setLandingHeroStats(p => ({ ...p, support: e.target.value }))} placeholder="e.g. 24/7" />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Revenue Handled</Label>
                          <Input value={landingHeroStats.revenue_handled} onChange={e => setLandingHeroStats(p => ({ ...p, revenue_handled: e.target.value }))} placeholder="e.g. 12L" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Network Section Stats */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Network / Map Section Stats</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <div>
                          <Label className="text-xs text-slate-500">Active Garages</Label>
                          <Input type="number" value={landingNetworkStats.active_garages} onChange={e => setLandingNetworkStats(p => ({ ...p, active_garages: e.target.value }))} placeholder="e.g. 8" />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Zones Covered</Label>
                          <Input type="number" value={landingNetworkStats.zones_covered} onChange={e => setLandingNetworkStats(p => ({ ...p, zones_covered: e.target.value }))} placeholder="e.g. 6" />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Cloud Support</Label>
                          <Input value={landingNetworkStats.cloud_support} onChange={e => setLandingNetworkStats(p => ({ ...p, cloud_support: e.target.value }))} placeholder="e.g. 24/7" />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Uptime</Label>
                          <Input value={landingNetworkStats.uptime} onChange={e => setLandingNetworkStats(p => ({ ...p, uptime: e.target.value }))} placeholder="e.g. 99.9%" />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Jobs Processed</Label>
                          <Input value={landingNetworkStats.jobs_processed} onChange={e => setLandingNetworkStats(p => ({ ...p, jobs_processed: e.target.value }))} placeholder="e.g. 15K" />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Avg Rating</Label>
                          <Input value={landingNetworkStats.avg_rating} onChange={e => setLandingNetworkStats(p => ({ ...p, avg_rating: e.target.value }))} placeholder="e.g. 4.8" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}

              {landingStatsMode === 'real' && (
                <Card>
                  <CardContent className="p-6 text-center text-slate-500">
                    <TrendingUp className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
                    <p className="font-medium">Real-time data mode is active</p>
                    <p className="text-sm mt-1">Stats on the landing page are automatically calculated from your database (active garages, jobs, revenue, etc.)</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* ═════════ HARDWARE TAB ═════════ */}
          <TabsContent value="hardware" className="mt-4">
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Monitor className="w-6 h-6 text-emerald-600" />
                  <h2 className="text-xl font-bold">Hardware Items</h2>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={fetchHardware} disabled={hwLoading}>
                    <RefreshCw className={`w-4 h-4 mr-1 ${hwLoading ? 'animate-spin' : ''}`} />Refresh
                  </Button>
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => { setHwEditing(null); setHwForm({ name: '', description: '', price: '', bundle_tag: '', category: '' }); setHwDialog(true); }}>
                    <Plus className="w-4 h-4 mr-1" />Add Item
                  </Button>
                </div>
              </div>

              {hwItems.length === 0 && !hwLoading && (
                <Card><CardContent className="p-8 text-center text-slate-400">
                  <Monitor className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                  <p className="font-medium">No hardware items yet</p>
                  <p className="text-sm mt-1">Add hardware items that garage owners might need to digitize their operations.</p>
                  <Button className="mt-4 bg-emerald-600 hover:bg-emerald-700" onClick={() => { setHwEditing(null); setHwForm({ name: '', description: '', price: '', bundle_tag: '', category: '' }); setHwDialog(true); }}>
                    <Plus className="w-4 h-4 mr-1" />Add First Item
                  </Button>
                </CardContent></Card>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {hwItems.map(item => (
                  <Card key={item.id} className={`overflow-hidden ${!item.is_active ? 'opacity-60' : ''}`}>
                    {(item.image || item.image_url) && (
                      <div className="aspect-video bg-slate-100 relative overflow-hidden">
                        <img src={item.image || item.image_url} alt={item.name} className="w-full h-full object-cover" />
                        {!item.is_active && <Badge className="absolute top-2 right-2 bg-red-500">Inactive</Badge>}
                      </div>
                    )}
                    <CardContent className="p-4 space-y-2">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-bold text-slate-900">{item.name}</h3>
                          {item.description && <p className="text-sm text-slate-500 mt-0.5">{item.description}</p>}
                        </div>
                        <span className="text-lg font-[900] text-emerald-600">৳{Number(item.price || 0).toLocaleString()}</span>
                      </div>
                      <div className="flex gap-1 flex-wrap">
                        {item.bundle_tag && <Badge className="bg-blue-50 text-blue-700 border-blue-200 text-[10px]">{item.bundle_tag}</Badge>}
                        {item.category && <Badge variant="outline" className="text-[10px]">{item.category}</Badge>}
                      </div>
                      <div className="flex gap-2 pt-2">
                        <label className="flex-1">
                          <input type="file" accept="image/*" className="hidden" onChange={e => { if (e.target.files[0]) uploadHardwareImage(item.id, e.target.files[0]); }} />
                          <Button variant="outline" size="sm" className="w-full" asChild><span><Upload className="w-3.5 h-3.5 mr-1" />Image</span></Button>
                        </label>
                        <Button variant="outline" size="sm" onClick={() => { setHwEditing(item.id); setHwForm({ name: item.name, description: item.description || '', price: item.price || '', bundle_tag: item.bundle_tag || '', category: item.category || '' }); setHwDialog(true); }}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="outline" size="sm" className="text-red-500 hover:text-red-700" onClick={() => deleteHardwareItem(item.id)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Hardware Orders */}
              <div className="mt-8">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold flex items-center gap-2"><Package className="w-5 h-5 text-emerald-600" />Hardware Orders</h3>
                  <Button variant="outline" size="sm" onClick={fetchHwOrders} disabled={hwOrdersLoading}>
                    <RefreshCw className={`w-4 h-4 mr-1 ${hwOrdersLoading ? 'animate-spin' : ''}`} />Refresh
                  </Button>
                </div>
                {hwOrders.length === 0 ? (
                  <Card><CardContent className="p-6 text-center text-slate-400 text-sm">No orders yet</CardContent></Card>
                ) : (
                  <div className="space-y-3">
                    {hwOrders.map(order => (
                      <Card key={order.id} className={`${order.status === 'cancelled' ? 'opacity-50' : ''}`}>
                        <CardContent className="p-4">
                          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-bold text-slate-900">{order.customer_name}</span>
                                <Badge className={
                                  order.status === 'pending' ? 'bg-yellow-100 text-yellow-700 border-yellow-200' :
                                  order.status === 'confirmed' ? 'bg-blue-100 text-blue-700 border-blue-200' :
                                  order.status === 'shipped' ? 'bg-purple-100 text-purple-700 border-purple-200' :
                                  order.status === 'delivered' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' :
                                  'bg-red-100 text-red-700 border-red-200'
                                }>{order.status}</Badge>
                              </div>
                              <p className="text-sm text-slate-500">{order.customer_phone} · {order.customer_address}</p>
                              <div className="flex flex-wrap gap-1 mt-2">
                                {(order.items || []).map((it, i) => (
                                  <Badge key={i} variant="outline" className="text-[10px]">{it.name} ×{it.qty}</Badge>
                                ))}
                              </div>
                              {order.notes && <p className="text-xs text-slate-400 mt-1">Note: {order.notes}</p>}
                              <p className="text-xs text-slate-400 mt-1">{new Date(order.created_at).toLocaleString()}</p>
                            </div>
                            <div className="text-right shrink-0">
                              <p className="text-lg font-[900] text-emerald-600">৳{Number(order.total || 0).toLocaleString()}</p>
                              {order.status === 'pending' && (
                                <div className="flex gap-1 mt-2">
                                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-xs h-7" onClick={() => updateHwOrderStatus(order.id, 'confirmed')}>Confirm</Button>
                                  <Button size="sm" variant="outline" className="text-red-500 text-xs h-7" onClick={() => updateHwOrderStatus(order.id, 'cancelled')}>Cancel</Button>
                                </div>
                              )}
                              {order.status === 'confirmed' && (
                                <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-xs h-7 mt-2" onClick={() => updateHwOrderStatus(order.id, 'shipped')}>Mark Shipped</Button>
                              )}
                              {order.status === 'shipped' && (
                                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-xs h-7 mt-2" onClick={() => updateHwOrderStatus(order.id, 'delivered')}>Mark Delivered</Button>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Hardware Create/Edit Dialog */}
            <Dialog open={hwDialog} onOpenChange={setHwDialog}>
              <DialogContent className="max-w-md">
                <DialogHeader><DialogTitle className="font-['Epilogue'] font-[800]">{hwEditing ? 'Edit' : 'Add'} Hardware Item</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label className="text-xs font-semibold text-slate-500">Name *</Label>
                    <Input value={hwForm.name} onChange={e => setHwForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Thermal Printer" className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-500">Description</Label>
                    <Textarea value={hwForm.description} onChange={e => setHwForm(p => ({ ...p, description: e.target.value }))} placeholder="Brief description..." className="mt-1" rows={2} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs font-semibold text-slate-500">Price (৳)</Label>
                      <Input type="number" value={hwForm.price} onChange={e => setHwForm(p => ({ ...p, price: e.target.value }))} placeholder="0" className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-slate-500">Category</Label>
                      <Input value={hwForm.category} onChange={e => setHwForm(p => ({ ...p, category: e.target.value }))} placeholder="e.g. Printing" className="mt-1" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-500">Bundle Tag</Label>
                    <Input value={hwForm.bundle_tag} onChange={e => setHwForm(p => ({ ...p, bundle_tag: e.target.value }))} placeholder="e.g. Budget Pack, Pro Pack" className="mt-1" />
                  </div>
                  <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={saveHardwareItem} disabled={!hwForm.name.trim()}>
                    {hwEditing ? 'Save Changes' : 'Create Item'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>
        </Tabs>

        {/* Delete Tenant Confirmation Dialog */}
        <Dialog open={!!deleteConfirm} onOpenChange={open => { if (!open) setDeleteConfirm(null); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-red-600">Permanently Delete Tenant</DialogTitle>
              <DialogDescription>
                This action is irreversible. All data including jobs, customers, invoices, parts, services, marketing credits, and user accounts will be permanently deleted.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Type <span className="font-bold text-red-600">{deleteConfirm?.name}</span> to confirm:
              </p>
              <Input
                value={deleteConfirm?.input || ''}
                onChange={e => setDeleteConfirm(prev => prev ? { ...prev, input: e.target.value } : prev)}
                placeholder="Type garage name to confirm"
                className="border-red-200 focus:border-red-400"
              />
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
                <Button
                  variant="destructive"
                  disabled={deleteConfirm?.input !== deleteConfirm?.name}
                  onClick={() => permanentlyDeleteTenant(deleteConfirm?.tenantId)}
                >
                  <Trash2 className="w-4 h-4 mr-1" /> Delete Forever
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
